import React from "react";
import { createPortal } from "react-dom";
import { supabase } from "./supabaseClient";
import {
  getParentCategoryForRaw,
  getSubcategoryDisplayName,
} from "./categoryTheme";
import {
  buildStoreUrl,
  buildQrStoreUrl,
  copyToClipboard,
  formatStoreLocation,
} from "./storeLinks";
import { QRCodeCanvas } from "qrcode.react";
import Stepper, { Step } from "./Stepper";
import Loader from "./Loader";
import { SmoothInput } from "./SmoothInput";
import SpeedingText from "./SpeedingText";
import ConfirmDialog from "./ConfirmDialog";
import {
  Home as HomeIcon,
  ClipboardList,
  UserCircle,
  BarChart3,
  LogOut,
  ArrowLeft,
  Store as StoreIcon,
  Copy,
  Check,
  Share2,
  MapPin,
  QrCode,
  Download,
  X as CloseIcon,
} from "lucide-react";

// --- DESCRIPTION GUARDRAILS ---
// Mirrored from SellerFlow (SF9) — keep both lists in sync if this changes.
// Prevents a seller from quoting a different price in the free-text
// description than what they actually set as the listing price.
const DESCRIPTION_MAX_LENGTH = 400;
const BANNED_DESCRIPTION_KEYWORDS = [
  "sh.",
  "kshs.",
  "ksh.",
  "shillings",
  "shilling",
  "kenya shillings",
  "kenyan shillings",
  "kes",
  "ksh",
  "kshs",
  "shs",
  "amount",
  "money",
  "price",
  "bob",
  "/=",
];

function stripDigits(value: string) {
  return value.replace(/[0-9]/g, "");
}

// The report tab's numbers, in display order. `key` matches the keys the
// get_seller_monthly_report database function returns.
const REPORT_METRICS: {
  key: string;
  label: string;
  hint: string;
  money?: boolean;
}[] = [
  { key: "views", label: "Listing views", hint: "Times buyers opened your listings" },
  { key: "contacts", label: "Contact taps", hint: "Buyers who tapped Call or WhatsApp" },
  { key: "bookings", label: "Bookings received", hint: "Booking requests from buyers" },
  { key: "sales", label: "Completed sales", hint: "Sales and bookings you marked completed" },
  { key: "earnings", label: "Earnings", hint: "Value of your completed sales", money: true },
];

const formatReportValue = (n: any, money?: boolean) => {
  const text = Math.round(Number(n || 0)).toLocaleString("en-KE");
  return money ? `KES ${text}` : text;
};

const formatReportRange = (start?: string, end?: string) => {
  if (!start || !end) return "";
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  const day = { day: "numeric", month: "short" } as const;
  return `${s.toLocaleDateString("en-GB", day)} – ${e.toLocaleDateString("en-GB", {
    ...day,
    year: "numeric",
  })}`;
};

// Shared by both the desktop tab row and the mobile bottom bar so they
// can never drift out of sync with each other.
const DASHBOARD_TABS: {
  id: "home" | "orders" | "store" | "report" | "account";
  label: string;
  icon: React.ComponentType<{ size?: number | string }>;
}[] = [
  { id: "home", label: "Home", icon: HomeIcon },
  { id: "orders", label: "Orders", icon: ClipboardList },
  { id: "store", label: "Store", icon: StoreIcon },
  { id: "report", label: "Report", icon: BarChart3 },
  { id: "account", label: "Account", icon: UserCircle },
];

function findBannedDescriptionKeyword(value: string) {
  const lower = value.toLowerCase();
  return (
    BANNED_DESCRIPTION_KEYWORDS.find((word: any) => lower.includes(word)) || null
  );
}

// --- CONTACT NUMBER FORMAT ---
// Phone/WhatsApp numbers are always stored as +254 followed by exactly 9
// digits (e.g. +254712345678 — the 10-digit local number 0712345678 with its
// leading 0 replaced by +254). That is the only shape the wa.me link built
// from them needs to always resolve to the right chat; a local-format number
// like "0721570179" or anything longer produces a broken WhatsApp link.
const PHONE_PREFIX = "+254";
const PHONE_DIGITS = 9;

function isValidKenyanPhone(value: string) {
  return /^\+254\d{9}$/.test(value.trim());
}

// "+254" with nothing after it isn't a number yet (shown as "Not set").
function hasPhoneDigits(value: string) {
  return value.trim().length > PHONE_PREFIX.length;
}

// Runs on every keystroke / paste in the number boxes, so a box can never
// hold anything but "+254" and up to 9 digits:
// - the +254 itself can't be deleted (it snaps back);
// - letters, spaces and symbols are dropped;
// - a leading 0 is dropped, because +254 is what replaces it
//   (typing or pasting 0712345678 gives +254712345678);
// - a number pasted with its own country code isn't doubled up;
// - a 10th digit is ignored.
function formatKenyanPhone(value: string): string {
  const raw = value.trim();
  if (raw === "" || PHONE_PREFIX.startsWith(raw)) return PHONE_PREFIX;
  let digits = raw.replace(/\D/g, "");
  // Typed or pasted after the +254 already in the box: drop that +254.
  if (raw.startsWith(PHONE_PREFIX)) digits = digits.slice(3);
  // A whole number pasted with its own country code (254 + 9 digits).
  if (digits.startsWith("254") && digits.length >= 12) digits = digits.slice(3);
  if (digits.startsWith("0")) digits = digits.slice(1);
  return PHONE_PREFIX + digits.slice(0, PHONE_DIGITS);
}

// For a number already saved: keep it if it's a real Kenyan number in any
// common shape (0712345678, 254712345678, +254712345678, 712345678), so it
// shows in the box correctly. Anything else (too long, too short, stray
// characters) comes up empty as "+254" so the seller types it again; the
// saved value stays untouched until they press Save.
function normalizeLoadedPhone(value: string): string {
  const digits = String(value || "").replace(/\D/g, "");
  if (/^254\d{9}$/.test(digits)) return PHONE_PREFIX + digits.slice(3);
  if (/^0\d{9}$/.test(digits)) return PHONE_PREFIX + digits.slice(1);
  if (/^\d{9}$/.test(digits)) return PHONE_PREFIX + digits;
  return PHONE_PREFIX;
}

// --- PER-CATEGORY DASHBOARD THEME ---
// Each parent category gets its own accent hue for the seller dashboard.
// Fashion & Beauty keeps the original orange the dashboard was built in;
// Furniture & Home Goods moves off brown (too muddy at dashboard scale)
// onto teal, matching the same swap made in categoryTheme.ts.
const CATEGORY_TAILWIND_FAMILY: Record<string, string> = {
  home_services: "sky",
  fashion_beauty: "orange",
  furniture_home_goods: "teal",
  food_drinks: "green",
  tech_electronics: "indigo",
  academic_services: "purple",
  events_creative: "pink",
};

// Hex values for the exact shades this dashboard actually uses, per
// Tailwind's default palette, for each of the 7 theme families.
const TAILWIND_PALETTE: Record<string, Record<number, string>> = {
  sky: {
    50: "#f0f9ff",
    100: "#e0f2fe",
    200: "#bae6fd",
    300: "#7dd3fc",
    400: "#38bdf8",
    500: "#0ea5e9",
    600: "#0284c7",
    700: "#0369a1",
    800: "#075985",
    900: "#0c4a6e",
    950: "#082f49",
  },
  orange: {
    50: "#fff7ed",
    100: "#ffedd5",
    200: "#fed7aa",
    300: "#fdba74",
    400: "#fb923c",
    500: "#f97316",
    600: "#ea580c",
    700: "#c2410c",
    800: "#9a3412",
    900: "#7c2d12",
    950: "#431407",
  },
  teal: {
    50: "#f0fdfa",
    100: "#ccfbf1",
    200: "#99f6e4",
    300: "#5eead4",
    400: "#2dd4bf",
    500: "#14b8a6",
    600: "#0d9488",
    700: "#0f766e",
    800: "#115e59",
    900: "#134e4a",
    950: "#042f2e",
  },
  green: {
    50: "#f0fdf4",
    100: "#dcfce7",
    200: "#bbf7d0",
    300: "#86efac",
    400: "#4ade80",
    500: "#22c55e",
    600: "#16a34a",
    700: "#15803d",
    800: "#166534",
    900: "#14532d",
    950: "#052e16",
  },
  indigo: {
    50: "#eef2ff",
    100: "#e0e7ff",
    200: "#c7d2fe",
    300: "#a5b4fc",
    400: "#818cf8",
    500: "#6366f1",
    600: "#4f46e5",
    700: "#4338ca",
    800: "#3730a3",
    900: "#312e81",
    950: "#1e1b4b",
  },
  purple: {
    50: "#faf5ff",
    100: "#f3e8ff",
    200: "#e9d5ff",
    300: "#d8b4fe",
    400: "#c084fc",
    500: "#a855f7",
    600: "#9333ea",
    700: "#7e22ce",
    800: "#6b21a8",
    900: "#581c87",
    950: "#3b0764",
  },
  pink: {
    50: "#fdf2f8",
    100: "#fce7f3",
    200: "#fbcfe8",
    300: "#f9a8d4",
    400: "#f472b6",
    500: "#ec4899",
    600: "#db2777",
    700: "#be185d",
    800: "#9d174d",
    900: "#831843",
    950: "#500724",
  },
  // Neutral placeholder used only before the seller's real category has
  // loaded (or been read from cache) — never an actual category's color, so
  // there's nothing to "flash away from" once the right one arrives.
  stone: {
    50: "#fafaf9",
    100: "#f5f5f4",
    200: "#e7e5e4",
    300: "#d6d3d1",
    400: "#a8a29e",
    500: "#78716c",
    600: "#57534e",
    700: "#44403c",
    800: "#292524",
    900: "#1c1917",
    950: "#0c0a09",
  },
};

const hexToRgb = (hex: string) => {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
};

// Every distinct (shade, alpha) combo actually referenced via var(--t-...)
// in this file's className strings — generated once per render, based on
// the logged-in seller's category family, and applied via inline style on
// the outermost wrapper so every nested Tailwind arbitrary-value class
// (e.g. bg-[var(--t-950-20)]) inherits it through normal CSS cascade.
const THEME_SHADE_ALPHA_PAIRS: [number, number | null][] = [
  [50, null],
  [100, null],
  [100, 40],
  [100, 70],
  [200, null],
  [200, 20],
  [200, 40],
  [200, 50],
  [200, 60],
  [200, 70],
  [200, 80],
  [300, null],
  [300, 60],
  [300, 70],
  [400, null],
  [500, null],
  [500, 15],
  [500, 20],
  [500, 30],
  [500, 40],
  [500, 60],
  [600, null],
  [700, null],
  [800, 30],
  [800, 40],
  [900, 20],
  [900, 30],
  [900, 40],
  [950, null],
  [950, 10],
  [950, 20],
  [950, 40],
  [950, 50],
];

function getDashboardThemeVars(familyKey: string): React.CSSProperties {
  const palette = TAILWIND_PALETTE[familyKey] || TAILWIND_PALETTE.orange;
  const vars: Record<string, string> = {};

  THEME_SHADE_ALPHA_PAIRS.forEach(([shade, alpha]) => {
    const varName = alpha ? `--t-${shade}-${alpha}` : `--t-${shade}`;
    const hex = palette[shade];
    vars[varName] = alpha ? `rgba(${hexToRgb(hex)}, ${alpha / 100})` : hex;
  });

  return vars as React.CSSProperties;
}

// Resolves a seller's stored category (subcategory id or parent id) to
// which Tailwind color family their dashboard should use. Falls back to
// "orange" — the family the dashboard was originally built in — for any
// seller without a recognized category yet.
export function getThemeFamilyForCategory(rawCategory?: string): string {
  const parent = getParentCategoryForRaw(rawCategory);
  if (parent && CATEGORY_TAILWIND_FAMILY[parent.id]) {
    return CATEGORY_TAILWIND_FAMILY[parent.id];
  }
  return "orange";
}

// --- HOME SERVICES SUBCATEGORY BEHAVIOR ---
// Home Services isn't one uniform listing type — different subcategories
// need different Create Listing rules:
//  - "laundry" is an open, walk-in-style service with no capacity concept;
//    it's expected to move fast, so it auto-expires 24h after posting.
//  - home_cleaning / pet_sitting / moving_help / errand_running / baby_sitting
//    are capped: the seller MUST state total slots, and the listing comes
//    down once every slot is completed (reuses the existing slots_total
//    mechanism). This is the only Home Services subcategory group besides
//    laundry — laundry is the sole exception with no slots concept at all.
// Every other category's services keep the original behavior: an optional,
// unlimited-by-default capacity field.
// Mirrored in App.tsx for buyer-feed filtering and the booking form — keep
// both in sync if this changes.
const HOME_SERVICES_OPEN_24H_SUBCATEGORIES = new Set(["laundry"]);
const HOME_SERVICES_SLOT_REQUIRED_SUBCATEGORIES = new Set([
  "home_cleaning",
  "pet_sitting",
  "moving_help",
  "errand_running",
  "baby_sitting",
]);

const AVATAR_PRESETS = [
  { id: "avatar_1", label: "Initial", color: "bg-[var(--t-500)]" },
  { id: "avatar_3", label: "Initial", color: "bg-emerald-600" },
  { id: "avatar_4", label: "Initial", color: "bg-purple-600" },
  { id: "avatar_5", label: "Initial", color: "bg-red-600" },
  { id: "avatar_6", label: "Initial", color: "bg-blue-600" },
];

// Small helper so any cached value we keep in localStorage is tagged with
// the auth user id that owns it. This is the core fix for the "deleted my
// account, re-applied under a different category, and the dashboard still
// showed my old display name" bug: plain localStorage keys like
// "seller_display_name" are scoped to the BROWSER, not the account, so they
// silently survive an account being deleted and leak into whatever account
// logs in next on that same device. Tagging the cached value with the uid
// it belongs to means a different (or freshly re-created) account — which
// always gets a different Supabase auth uid — simply won't match, so it
// falls through to the real default instead of showing stale data.
function readOwnedCache(key: string, uid: string | null): string | null {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { uid?: string; value?: string };
    if (parsed.uid !== uid) return null;
    return parsed.value ?? null;
  } catch {
    // Covers both a missing key and a pre-fix plain-string value left over
    // from before this change — either way, treat it as "no valid cache".
    return null;
  }
}

function writeOwnedCache(key: string, uid: string, value: string): void {
  try {
    localStorage.setItem(key, JSON.stringify({ uid, value }));
  } catch {
    // Best-effort only — a failed write just means no local cache, never a
    // correctness issue since the database stays the source of truth.
  }
}

// --- COACHMARKS (guided first-visit walkthrough for new sellers) ---
// Self-contained copy of the same pattern used on the buyer side (see
// App.tsx): a spotlight cutout around a target element plus a themed
// tooltip pointing at it, shown step-by-step the first time a NEW seller
// lands on a given tab. Kept fully self-contained here (rather than
// imported from App.tsx) to avoid a circular import — App.tsx already
// imports SellerPortal.tsx, so the reverse would create a loop.
type CoachStep = {
  ref: React.RefObject<HTMLElement>;
  title: string;
  text: string;
};

// True only for a seller account Supabase has JUST created. Same
// created_at vs last_sign_in_at heuristic as the buyer side: on a brand
// new signup those two timestamps land within moments of each other; on
// any later login, last_sign_in_at has moved well past created_at. This
// makes the walkthrough follow the ACCOUNT rather than the device.
function useIsNewSeller() {
  const [isNew, setIsNew] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Manual override for testing: append ?debugNewBuyer=1 to the URL
        // (same flag the buyer side uses) to force every coachmark to
        // show without needing a fresh Supabase signup each time.
        if (
          typeof window !== "undefined" &&
          window.location.search.includes("debugNewBuyer=1")
        ) {
          if (!cancelled) setIsNew(true);
          return;
        }
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        const createdAt = new Date(user.created_at).getTime();
        const lastSignInAt = user.last_sign_in_at
          ? new Date(user.last_sign_in_at).getTime()
          : createdAt;
        if (!cancelled) {
          setIsNew(Math.abs(lastSignInAt - createdAt) < 5 * 60 * 1000);
        }
      } catch (err) {
        console.error("[Coachmarks] useIsNewSeller check failed:", err);
        if (!cancelled) setIsNew(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return isNew;
}

function useCoachTour(key: string, steps: CoachStep[], enabled = true) {
  const [stepIndex, setStepIndex] = React.useState(-1);
  const storageKey = `inua_seller_coach_seen_${key}`;

  React.useEffect(() => {
    if (!enabled || steps.length === 0) return;
    let seen = false;
    try {
      seen = !!localStorage.getItem(storageKey);
    } catch {
      seen = true;
    }
    if (seen) return;
    const t = setTimeout(() => setStepIndex(0), 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, storageKey, steps.length]);

  const finish = () => {
    try {
      localStorage.setItem(storageKey, "1");
    } catch {}
    setStepIndex(-1);
  };

  const next = () => {
    setStepIndex((i) => (i + 1 >= steps.length ? -1 : i + 1));
    if (stepIndex + 1 >= steps.length) finish();
  };

  return {
    activeStep: stepIndex >= 0 ? steps[stepIndex] : null,
    stepIndex,
    totalSteps: steps.length,
    next,
    dismiss: finish,
  };
}

// --- STAGED COACH TOUR (steps that can be "saved for later") ---
// useCoachTour above treats a tour as all-or-nothing: once it has run, it
// never runs again. That can't work for the Store tab, where some steps
// aren't possible yet (the listing callouts need a first listing to point
// at) and others get added after some sellers have already seen the tour
// (the QR code). This version remembers each step on its own, so:
//   - a step that isn't available yet is simply left out and stays "unseen"
//     until it IS available, then shows the next time the seller is on the tab;
//   - steps already seen are never repeated;
//   - leaving the tab mid-tour pauses it without marking anything seen;
//   - Skip marks only the steps that were queued, not the ones still waiting.
// `eligible` is the usual new-seller check, but once this device has started
// a tour the seller stays eligible for its remaining steps, so a seller who
// creates their first listing in a later session still gets the callouts.
type StagedCoachStep = CoachStep & { id: string };

const STAGED_TOUR_PREFIX = "inua_seller_coach_steps_";

function readSeenCoachIds(
  key: string,
  legacySeenIds: string[]
): string[] | null {
  try {
    let ids: string[] = [];
    const raw = localStorage.getItem(`${STAGED_TOUR_PREFIX}${key}`);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        ids = parsed.filter((x: unknown): x is string => typeof x === "string");
      }
    }
    // The older all-or-nothing flag: those tours' steps count as seen.
    if (localStorage.getItem(`inua_seller_coach_seen_${key}`)) {
      ids = Array.from(new Set([...ids, ...legacySeenIds]));
    }
    return ids;
  } catch {
    // Storage unavailable (private browsing, etc.) — skip the tour rather
    // than showing it every single time.
    return null;
  }
}

function writeSeenCoachIds(key: string, ids: string[]) {
  try {
    localStorage.setItem(`${STAGED_TOUR_PREFIX}${key}`, JSON.stringify(ids));
  } catch {}
}

function useStagedCoachTour(
  key: string,
  steps: StagedCoachStep[],
  opts: {
    active: boolean; // the screen is ready, so a tour may START now
    present: boolean; // the screen is still showing (leaving pauses the tour)
    eligible: boolean; // new-seller check
    legacySeenIds?: string[];
  }
) {
  const { active, present, eligible } = opts;
  const legacySeenIds = opts.legacySeenIds || [];
  const [stepIndex, setStepIndex] = React.useState(-1);
  const [queue, setQueue] = React.useState<StagedCoachStep[]>([]);
  const runningRef = React.useRef(false);
  const stepsRef = React.useRef<StagedCoachStep[]>(steps);
  stepsRef.current = steps;
  const idsSignature = steps.map((s) => s.id).join("|");

  React.useEffect(() => {
    if (!active || runningRef.current || !idsSignature) return;
    const seenNow = readSeenCoachIds(key, legacySeenIds);
    if (seenNow === null) return;
    if (!eligible && seenNow.length === 0) return;
    // Give the screen's own layout a beat to settle before measuring.
    const t = setTimeout(() => {
      if (runningRef.current) return;
      const seen = readSeenCoachIds(key, legacySeenIds) || [];
      const unseen = stepsRef.current.filter((s) => !seen.includes(s.id));
      if (unseen.length === 0) return;
      runningRef.current = true;
      setQueue(unseen);
      setStepIndex(0);
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, eligible, key, idsSignature]);

  // Left the screen mid-tour: pause it, and keep what's unseen for next time.
  React.useEffect(() => {
    if (present || !runningRef.current) return;
    runningRef.current = false;
    setStepIndex(-1);
    setQueue([]);
  }, [present]);

  const persist = (ids: string[]) => {
    const seen = readSeenCoachIds(key, legacySeenIds) || [];
    writeSeenCoachIds(key, Array.from(new Set([...seen, ...ids])));
  };

  const end = () => {
    runningRef.current = false;
    setStepIndex(-1);
    setQueue([]);
  };

  const next = () => {
    const current = queue[stepIndex];
    if (current) persist([current.id]);
    if (stepIndex + 1 >= queue.length) end();
    else setStepIndex(stepIndex + 1);
  };

  const dismiss = () => {
    persist(queue.map((s) => s.id));
    end();
  };

  return {
    activeStep: stepIndex >= 0 ? queue[stepIndex] || null : null,
    stepIndex,
    totalSteps: queue.length,
    next,
    dismiss,
  };
}

function Coachmark({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onDismiss,
}: {
  step: CoachStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onDismiss: () => void;
}) {
  const [rect, setRect] = React.useState<DOMRect | null>(null);
  // Two-pass positioning: the tooltip's real height isn't known until it
  // renders (title/body text wraps differently per step), so choosing
  // above-vs-below ahead of time can still push it off the bottom (or
  // top) of the screen — this is what made Skip/Next unreachable.
  // tooltipHeight starts as a conservative estimate for the first paint,
  // then gets corrected to the real measured height right after.
  const [tooltipHeight, setTooltipHeight] = React.useState(140);
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const measure = () => {
      if (step.ref.current) setRect(step.ref.current.getBoundingClientRect());
    };
    // Long pages (like the Store tab's listing cards) can have a target
    // that's below the fold or tucked behind the bottom tab bar. If the
    // target isn't fully on-screen, bring it to the middle first so the
    // spotlight and tooltip always have something visible to point at.
    // Targets that are already fully visible are left exactly where they are.
    const target = step.ref.current;
    if (target) {
      const r = target.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) {
        target.scrollIntoView({
          block: "center",
          inline: "nearest",
          behavior: "auto",
        });
      }
    }
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [step]);

  React.useLayoutEffect(() => {
    if (tooltipRef.current) {
      setTooltipHeight(tooltipRef.current.getBoundingClientRect().height);
    }
  }, [step, rect]);

  if (!rect) return null;

  const pad = 6;
  const margin = 12;
  const viewportH = window.innerHeight;
  const viewportW = window.innerWidth;
  const tooltipWidth = 272;
  const left = Math.min(
    Math.max(rect.left + rect.width / 2 - tooltipWidth / 2, margin),
    viewportW - tooltipWidth - margin
  );

  // Prefer below the target; only place above if there's genuinely more
  // room there. Either way, clamp against the tooltip's own (measured)
  // height so it always stays fully on-screen, buttons included.
  const spaceBelow = viewportH - rect.bottom;
  const spaceAbove = rect.top;
  const placeBelow = spaceBelow >= spaceAbove;
  let top = placeBelow
    ? rect.bottom + pad + 12
    : rect.top - pad - 12 - tooltipHeight;
  top = Math.min(Math.max(top, margin), viewportH - tooltipHeight - margin);

  // This dashboard is always dark (its per-category "theme family" only
  // changes the accent hue, never the light/dark base) — so, unlike the
  // buyer side's Coachmark, this one can safely hardcode a single dark
  // tooltip style rather than branching on a theme value.
  return createPortal(
    <div className="fixed inset-0 z-[9998]">
      <div
        onClick={onDismiss}
        style={{
          position: "fixed",
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
          borderRadius: 14,
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.75)",
          border: "2px solid #fff",
          pointerEvents: "auto",
          transition: "all 0.25s ease",
        }}
      />
      <div
        ref={tooltipRef}
        style={{
          position: "fixed",
          top,
          left,
          width: tooltipWidth,
          maxHeight: viewportH - margin * 2,
          overflowY: "auto",
          backgroundColor: "#101018",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
        }}
        className="rounded-2xl p-4"
      >
        <p className="font-bold text-sm mb-1 text-white">{step.title}</p>
        <p className="text-xs leading-relaxed mb-3 text-[#C4C4D4]">
          {step.text}
        </p>
        <div className="flex items-center justify-between">
          <button
            onClick={onDismiss}
            className="text-[10px] font-semibold uppercase tracking-wider text-[#8888A8] hover:text-[#C4C4D4] transition-colors"
          >
            Skip
          </button>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[#8888A8]">
              {stepIndex + 1}/{totalSteps}
            </span>
            <button
              onClick={onNext}
              style={{ backgroundColor: "#ffb800" }}
              className="px-4 py-1.5 rounded-lg text-[#101018] text-xs font-bold hover:brightness-110 active:brightness-95 transition"
            >
              {stepIndex === totalSteps - 1 ? "Done" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function ActiveSellerDashboard({
  onNewListingClick,
  navigateTo,
  onInitialLoadComplete,
  onRequestHomeTransition,
}: any) {
  // No localStorage read here on purpose — at this point we don't yet know
  // which account is signed in, so there's nothing safe to validate a
  // cached value against. The real name loads a moment later in
  // fetchSellerProfile below, from an ownership-tagged cache (or the DB).
  const [sellerName, setSellerName] = React.useState("Seller");
  const [phone, setPhone] = React.useState("");
  const [whatsapp, setWhatsapp] = React.useState("");
  const [avatarId, setAvatarId] = React.useState("avatar_1");

  const [originalDbData, setOriginalDbData] = React.useState({
    display_name: "",
    phone_number: "",
    whatsapp_number: "",
    avatar_id: "avatar_1",
  });
  // True once the seller has an actual display_name saved in the database.
  // Once true, the Display Name field becomes permanently locked.
  const [isDisplayNameLocked, setIsDisplayNameLocked] = React.useState(false);
  const [displayNameError, setDisplayNameError] = React.useState<string | null>(
    null
  );
  const [isCheckingDisplayName, setIsCheckingDisplayName] =
    React.useState(false);

  // "product" or "service" — captured at seller signup (SellerFlow). Drives
  // which Create Listing fields, metric labels, and listing-row actions show.
  const [sellerListingType, setSellerListingType] = React.useState<
    "product" | "service"
  >("product");

  // Which Tailwind color family drives this dashboard's entire accent theme,
  // derived from the seller's own category. Starts neutral for every seller
  // — including returning ones — since we can't validate an ownership-tagged
  // cache until fetchSellerProfile knows the signed-in uid a moment later.
  // That's a one-frame flash of "stone" instead of an instant color, traded
  // for guaranteeing a brand-new/different account never briefly shows a
  // previous seller's theme.
  const [themeFamily, setThemeFamily] = React.useState<string>("stone");
  const themeVars = React.useMemo(
    () => getDashboardThemeVars(themeFamily),
    [themeFamily]
  );

  // Fires `onInitialLoadComplete` (the parent SellerPortal's cue that it's
  // safe to drop the "One moment..." overlay) once BOTH of this
  // dashboard's independent mount-time fetches — listings/orders/bookings
  // and the seller profile — have completed at least once. Guarded by a
  // ref so realtime refetches later (new orders/bookings coming in) never
  // re-fire it; this is strictly a "first paint is ready" signal.
  const [initialListingsLoaded, setInitialListingsLoaded] =
    React.useState(false);
  const [initialProfileLoaded, setInitialProfileLoaded] = React.useState(false);
  const hasFiredInitialLoadRef = React.useRef(false);
  React.useEffect(() => {
    if (
      initialListingsLoaded &&
      initialProfileLoaded &&
      !hasFiredInitialLoadRef.current
    ) {
      hasFiredInitialLoadRef.current = true;
      const hex = (TAILWIND_PALETTE[themeFamily] ||
        TAILWIND_PALETTE.orange)[500];
      onInitialLoadComplete?.(hex);
    }
  }, [
    initialListingsLoaded,
    initialProfileLoaded,
    onInitialLoadComplete,
    themeFamily,
  ]);

  // The seller's own resolved parent category (e.g. Home Services), used to
  // populate the subcategory picker in Create Listing with the right options
  // (Laundry, Pet Sitting, etc.) for THIS seller specifically.
  const [sellerParentCategory, setSellerParentCategory] = React.useState<
    ReturnType<typeof getParentCategoryForRaw> | undefined
  >(undefined);
  const [selectedSubcategory, setSelectedSubcategory] = React.useState("");

  // Buyer demand for this seller's category: subcategories where enough buyers
  // (5+) used "Make a request". Comes from the get_category_demand database
  // function, which returns only counts, counties and short anonymised
  // descriptions — never who asked.
  const [demand, setDemand] = React.useState<any[]>([]);
  React.useEffect(() => {
    const catId = sellerParentCategory?.id;
    if (!catId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc("get_category_demand", {
          p_category: catId,
        });
        if (!cancelled && !error && Array.isArray(data)) setDemand(data);
      } catch {
        // Nice-to-have card: stay hidden if it cannot load.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sellerParentCategory?.id]);

  const [totalValueGenerated, setTotalValueGenerated] = React.useState(0);
  // null means "N/A" — every active listing is an open/uncapped service
  // (no slots_total), so there's no bounded number of future bookings to
  // project. See fetchDashboardData below for how this is decided.
  const [potentialShopValue, setPotentialShopValue] = React.useState<
    number | null
  >(0);
  const [listings, setListings] = React.useState<any[]>([]);
  const [isLoadingDashboard, setIsLoadingDashboard] = React.useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  // Which top-level dashboard tab is showing. "home" is this same
  // overview screen (metrics + active listings) that used to be the
  // dashboard's only view; "orders" and "account" are new.
  const [activeTab, setActiveTab] = React.useState<
    "home" | "orders" | "store" | "report" | "account"
  >("home");

  // --- REPORT TAB ---
  // Loaded only while the Report tab is open. "last" = the previous calendar
  // month; "this" = the current month so far.
  const [reportPeriod, setReportPeriod] = React.useState<"last" | "this">("last");
  const [report, setReport] = React.useState<any>(null);
  const [reportStatus, setReportStatus] = React.useState<"idle" | "loading" | "error">("idle");
  const [reportReloadKey, setReportReloadKey] = React.useState(0);
  React.useEffect(() => {
    if (activeTab !== "report") return;
    let cancelled = false;
    setReportStatus("loading");
    (async () => {
      try {
        const args =
          reportPeriod === "this"
            ? {
                p_month: new Date().toLocaleDateString("en-CA", {
                  timeZone: "Africa/Nairobi",
                }),
              }
            : {};
        const { data, error } = await supabase.rpc(
          "get_seller_monthly_report",
          args
        );
        if (cancelled) return;
        if (error || !data) {
          setReport(null);
          setReportStatus("error");
          return;
        }
        setReport(data);
        setReportStatus("idle");
      } catch {
        if (!cancelled) {
          setReport(null);
          setReportStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, reportPeriod, reportReloadKey]);

  // --- STORE TAB ---
  // The seller's random store code (see store_code.sql) and the location line
  // buyers see on the public store page. Fetched on its own, separately from
  // fetchSellerProfile, so a missing store_code column can never break the
  // rest of the dashboard's profile load.
  const [storeCode, setStoreCode] = React.useState("");
  const [storeInfoLoaded, setStoreInfoLoaded] = React.useState(false);
  const [storeLocationLine, setStoreLocationLine] = React.useState("");
  const [storeLinkCopied, setStoreLinkCopied] = React.useState(false);
  const [isQrSheetOpen, setIsQrSheetOpen] = React.useState(false);
  const qrCanvasRef = React.useRef<HTMLCanvasElement>(null);

  // --- COACHMARK REFS + TOURS (see Coachmark/useCoachTour above) ---
  const isNewSeller = useIsNewSeller();
  // Same "is the dashboard actually ready to look at" signal the effect
  // above uses to fire onInitialLoadComplete (which tells the PARENT to
  // drop its own "Fetching your data..." overlay). Gating every tour on
  // this too — not just this component's own isLoadingDashboard — is
  // what stops a coachmark from spotlighting real elements while they're
  // still hidden behind that parent-level loading screen.
  const isDashboardReady = initialListingsLoaded && initialProfileLoaded;
  const homeDisplayNameRef = React.useRef<HTMLDivElement>(null);
  const homeCreateListingBtnRef = React.useRef<HTMLButtonElement>(null);
  const homePotentialValueRef = React.useRef<HTMLDivElement>(null);
  const homeActiveListingsRef = React.useRef<HTMLDivElement>(null);
  const homeTotalEarningsRef = React.useRef<HTMLDivElement>(null);
  const homeTotalClicksRef = React.useRef<HTMLDivElement>(null);
  const sellerHomeTour = useCoachTour(
    "home",
    [
      {
        ref: homeDisplayNameRef,
        title: "Your Shop",
        text: "This is your seller display name — buyers see this on every listing you post.",
      },
      {
        ref: homePotentialValueRef,
        title: "Potential Shop Value",
        text: "The combined value of every listing you currently have live.",
      },
      {
        ref: homeActiveListingsRef,
        title: "Active Listings",
        text: "How many of your listings are currently live and visible to buyers.",
      },
      {
        ref: homeTotalEarningsRef,
        title: "Total Earnings",
        text: "Everything you've earned so far from items marked SOLD or completed bookings.",
      },
      {
        ref: homeTotalClicksRef,
        title: "Total Item Clicks",
        text: "How many times buyers have opened the detail view on your listings.",
      },
      {
        ref: homeCreateListingBtnRef,
        title: "Create New Listing",
        text: "Tap here anytime to add a new product or service to your shop.",
      },
    ],
    isNewSeller && isDashboardReady && activeTab === "home"
  );

  const ordersPendingRef = React.useRef<HTMLDivElement>(null);
  const ordersRecentRef = React.useRef<HTMLDivElement>(null);
  const sellerOrdersTour = useCoachTour(
    "orders",
    [
      {
        ref: ordersPendingRef,
        title: "Current & Pending Orders",
        text: "New booking requests or orders waiting on you show up here.",
      },
      {
        ref: ordersRecentRef,
        title: "Recent Orders",
        text: "Your last 10 completed orders and bookings, for a quick history.",
      },
    ],
    isNewSeller && isDashboardReady && activeTab === "orders"
  );

  const accountEditCardRef = React.useRef<HTMLDivElement>(null);
  const accountOverviewRef = React.useRef<HTMLDivElement>(null);
  const sellerAccountTour = useCoachTour(
    "account",
    [
      {
        ref: accountEditCardRef,
        title: "Edit Contact Card",
        text: "These fields make up your public business card — this is exactly what buyers see and use to contact you.",
      },
      {
        ref: accountOverviewRef,
        title: "Account Overview",
        text: "A live preview of your current business card, so you can check it without saving or reloading.",
      },
    ],
    isNewSeller && isDashboardReady && activeTab === "account"
  );

  // Store tab tour (staged — see useStagedCoachTour). The Share step only
  // exists when the browser supports the native share sheet (same condition
  // that renders the Share button). The two listing callouts only exist once
  // there's at least one listing to point at; until then they are saved for
  // later and play the first time the seller opens the Store tab with a
  // listing, however many sessions that takes.
  const storeLinkCardRef = React.useRef<HTMLDivElement>(null);
  const storeCopyBtnRef = React.useRef<HTMLButtonElement>(null);
  const storeShareBtnRef = React.useRef<HTMLButtonElement>(null);
  const storeQrBtnRef = React.useRef<HTMLButtonElement>(null);
  const storeHeaderRef = React.useRef<HTMLDivElement>(null);
  const storeFirstPhotosRef = React.useRef<HTMLDivElement>(null);
  const storeFirstDetailsRef = React.useRef<HTMLDivElement>(null);
  // QR code — encodes the same /store/<code> link as Copy/Share, but always on
  // the live site's address (see PUBLIC_SITE_ORIGIN in storeLinks.ts). It
  // carries nothing except that link, so there's nothing private in it and it
  // never goes stale: the store page loads the seller's current listings every
  // time it's opened.
  const storeQrUrl = storeCode ? buildQrStoreUrl(storeCode) : "";

  React.useEffect(() => {
    if (!isQrSheetOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsQrSheetOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isQrSheetOpen]);

  const handleDownloadQr = () => {
    const qr = qrCanvasRef.current;
    if (!qr || !storeQrUrl) return;
    // Print-ready card: white background with a clear margin around the code
    // (scanners need that quiet zone), then the store name and a short line.
    const W = 1000;
    const pad = 100;
    const qrSize = W - pad * 2;
    const H = pad + qrSize + 220;
    const out = document.createElement("canvas");
    out.width = W;
    out.height = H;
    const ctx = out.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, H);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(qr, pad, pad, qrSize, qrSize);

    const fontStack =
      "system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif";
    const name = (sellerName || "INUA Seller").trim();
    ctx.fillStyle = "#111111";
    ctx.textAlign = "center";
    let size = 60;
    ctx.font = `bold ${size}px ${fontStack}`;
    while (ctx.measureText(name).width > W - pad * 2 && size > 28) {
      size -= 2;
      ctx.font = `bold ${size}px ${fontStack}`;
    }
    ctx.fillText(name, W / 2, pad + qrSize + 90);
    ctx.fillStyle = "#555555";
    ctx.font = `34px ${fontStack}`;
    ctx.fillText("Scan to browse my store on INUA", W / 2, pad + qrSize + 150);

    const slug =
      name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 40) || "store";
    out.toBlob((blob) => {
      if (!blob) {
        showAppToast({
          title: "Couldn't save",
          message: "Take a screenshot of the QR code instead.",
        });
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `inua-store-qr-${slug}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, "image/png");
  };

  const storeCanNativeShare =
    !!storeCode &&
    typeof navigator !== "undefined" &&
    typeof (navigator as any).share === "function";
  const storeHasListings = !isLoadingDashboard && listings.length > 0;
  const sellerStoreTour = useStagedCoachTour(
    "store",
    [
      {
        id: "link",
        ref: storeLinkCardRef,
        title: "Your Store Link",
        text: "This is your personal storefront link. Anyone you send it to can browse all of your listings in one place.",
      },
      {
        id: "copy",
        ref: storeCopyBtnRef,
        title: "Copy Your Link",
        text: "Tap Copy, then paste your link into WhatsApp, Instagram or your status. Tapping the link itself copies it too.",
      },
      ...(storeCanNativeShare
        ? [
            {
              id: "share",
              ref: storeShareBtnRef,
              title: "Share Instantly",
              text: "Use Share to send your link straight to your contacts or apps without copying it first.",
            },
          ]
        : []),
      {
        id: "qr",
        ref: storeQrBtnRef,
        title: "Your QR Code",
        text: "Tap QR Code to show a scannable code for your store. Buyers scan it with their phone camera and land on your store. You can download it to print or post.",
      },
      {
        id: "header",
        ref: storeHeaderRef,
        title: "Your Storefront",
        text: "This is how your store opens for buyers — your display name and location. They never see your orders, earnings or account details.",
      },
      ...(storeHasListings
        ? [
            {
              id: "photos",
              ref: storeFirstPhotosRef,
              title: "Listing Photos",
              text: "Each listing shows its photos here. Buyers can swipe through all of them, so make your first photo count.",
            },
            {
              id: "details",
              ref: storeFirstDetailsRef,
              title: "Full Listing Details",
              text: "Every listing appears in full — status, price, description, availability and views. Buyers see a lighter version of this on your public store.",
            },
          ]
        : []),
    ],
    {
      active:
        isDashboardReady &&
        activeTab === "store" &&
        storeInfoLoaded &&
        !isLoadingDashboard &&
        !isQrSheetOpen,
      present: activeTab === "store",
      eligible: isNewSeller,
      // Steps the older single-flag version of this tour already covered.
      legacySeenIds: ["link", "copy", "share", "header"],
    }
  );

  // Generic "are you sure?" dialog shared by every destructive/confirming
  // action in this dashboard (delete listing, confirm booking, ...). Each
  // call site fills in its own copy + confirmText + what onConfirm does;
  // the dialog component itself stays fully generic.
  const [confirmDialog, setConfirmDialog] = React.useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    isDanger: boolean;
    onConfirm: () => void;
  } | null>(null);

  const closeConfirmDialog = () =>
    setConfirmDialog((prev) => (prev ? { ...prev, isOpen: false } : prev));
  const [isSuccessScreen, setIsSuccessScreen] = React.useState(false);
  const [productName, setProductName] = React.useState("");

  // App-wide toast replacing native browser alert() popups. Any validation
  // message or error goes through showAppToast instead — same visual
  // language everywhere, with an optional action button (used for the
  // "Contact card required" case, which links straight to fixing it).
  const [appToast, setAppToast] = React.useState<{
    title: string;
    message: string;
    variant?: "warning" | "success" | "info";
    durationMs?: number;
    action?: { label: string; onClick: () => void };
  } | null>(null);
  const toastTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const showAppToast = (
    toast: {
      title: string;
      message: string;
      variant?: "warning" | "success" | "info";
      action?: { label: string; onClick: () => void };
    },
    durationMs = 6000
  ) => {
    setAppToast({ variant: "warning", ...toast, durationMs });
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => {
      setAppToast(null);
    }, durationMs);
  };

  const triggerContactCardToast = () => {
    showAppToast(
      {
        title: "Contact card required",
        message:
          "Buyers need a way to reach you before you can create a listing. Visit your Account tab to edit your details.",
        action: {
          label: "Go to Account →",
          onClick: () => setActiveTab("account"),
        },
      },
      12000
    );
  };

  React.useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const [productPrice, setProductPrice] = React.useState("");
  const [productDescription, setProductDescription] = React.useState("");
  const [listingType, setListingType] = React.useState<"LIVE" | "PRE_ORDER">(
    "LIVE"
  );
  const [preorderUnits, setPreorderUnits] = React.useState("5");
  // How many physical units the seller has on hand for a LIVE NOW product
  // listing. Saved as listings.stock_quantity — the buyer feed already
  // reads this to show an "Only X left" badge once it drops to 1 or 2, and
  // hides the count entirely otherwise (see App.tsx's product detail view).
  const [unitsToSell, setUnitsToSell] = React.useState("1");
  const [selectedImages, setSelectedImages] = React.useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = React.useState<string[]>([]);
  // Tracks, per-preview, whether the browser has actually finished decoding
  // that image yet. Previews render instantly via createObjectURL, but large
  // photos can take a moment to decode/paint — this drives a "still loading"
  // pulse on each thumbnail (like Claude's own image-attach UX) so the seller
  // never wonders whether their pick registered.
  const [loadedPreviews, setLoadedPreviews] = React.useState<boolean[]>([]);
  const [isUploading, setIsUploading] = React.useState(false);

  // SLOW UPLOAD NOTE — if publishing is still running after a few seconds
  // (big photos / slow data), show a short pop-up so the seller doesn't
  // think the app froze or tap INUA again.
  React.useEffect(() => {
    if (!isUploading) return;
    const t = setTimeout(() => {
      showAppToast(
        {
          title: "This might take a while",
          message:
            "Your photos are still uploading. Please keep this page open.",
          variant: "info",
        },
        8000
      );
    }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploading]);

  // --- SERVICE-ONLY LISTING FIELDS (shown when sellerListingType === "service") ---
  const [serviceArea, setServiceArea] = React.useState("");
  const [turnaround, setTurnaround] = React.useState("");
  const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const [availDays, setAvailDays] = React.useState<string[]>([
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
  ]);
  const [availStart, setAvailStart] = React.useState("09:00");
  const [availEnd, setAvailEnd] = React.useState("17:00");
  const [slotMinutes, setSlotMinutes] = React.useState(60);
  // How many total bookings this specific listing can fulfill before it's
  // pulled from the buyer feed automatically — e.g. a "Rihanna Locs" listing
  // capped at 3 slots comes down once all 3 are marked completed, so the
  // seller can put up a fresh listing for their next style without an old,
  // no-longer-relevant one lingering in front of buyers.
  const [capacitySlots, setCapacitySlots] = React.useState("");

  const toggleAvailDay = (day: string) => {
    setAvailDays((prev) =>
      prev.includes(day) ? prev.filter((d: any) => d !== day) : [...prev, day]
    );
  };

  // --- BOOKINGS MANAGEMENT (service listings) ---
  const [isBookingsModalOpen, setIsBookingsModalOpen] = React.useState(false);
  const [activeBookingsListing, setActiveBookingsListing] =
    React.useState<any>(null);
  const [bookings, setBookings] = React.useState<any[]>([]);
  const [isLoadingBookings, setIsLoadingBookings] = React.useState(false);
  // Count of pending booking requests per listing_id, so the "Bookings"
  // action button can show a badge without opening the modal first.
  const [pendingBookingsByListing, setPendingBookingsByListing] =
    React.useState<Record<string, number>>({});

  // --- ORDER HISTORY (Orders tab) ---
  // Every row in `orders` for this seller — both one-off "Sell 1 Unit"
  // sales and completed service bookings insert here (see
  // handleBookingStatusChange below) — so this is "Completed Orders",
  // full sales history rather than just the day's activity.
  //
  // Deliberately NOT joined to `listings` here (no second query, no
  // `listings:listing_id(...)` embed) — the listing's title/image are
  // looked up against the `listings` array this dashboard already loads
  // via fetchDashboardData (below), which is known-good since it's what
  // renders the Active Listings table. A listing referenced by an order
  // can never actually be deleted (handleDeleteListing blocks deleting
  // anything with sale history), so that lookup should always hit;
  // rendering falls back to "Listing removed" only if it somehow doesn't.
  const [sellerOrders, setSellerOrders] = React.useState<any[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = React.useState(false);
  const hasLoadedOrdersRef = React.useRef(false);

  const fetchSellerOrders = React.useCallback(async () => {
    setIsLoadingOrders(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setSellerOrders([]);
        return;
      }

      // Only the 10 most recent transactions — the Orders tab shows a
      // quick recent-activity snapshot, not the full sales history, so
      // there's no need to pull (and render) every row the seller has
      // ever had.
      const { data, error } = await supabase
        .from("orders")
        .select("id, listing_id, amount, created_at")
        .eq("seller_id", user.id)
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) {
        console.error("[Orders] Failed to load order history:", error);
        setSellerOrders([]);
        return;
      }

      setSellerOrders(data || []);
    } catch (err) {
      console.error("Error loading order history:", err);
      setSellerOrders([]);
    } finally {
      setIsLoadingOrders(false);
    }
  }, []);

  // Lazy-load the first time the Orders tab is opened (no need to fetch
  // this up front alongside the dashboard's own listings/earnings query).
  React.useEffect(() => {
    if (activeTab !== "orders" || hasLoadedOrdersRef.current) return;
    hasLoadedOrdersRef.current = true;
    fetchSellerOrders();
  }, [activeTab, fetchSellerOrders]);

  // Keep it live once loaded — a sale made from another tab/device (or a
  // booking marked completed) should show up here without a manual
  // refresh.
  React.useEffect(() => {
    const channel = supabase
      .channel("seller-orders-tab-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders" },
        () => {
          if (hasLoadedOrdersRef.current) fetchSellerOrders();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchSellerOrders]);

  // --- CURRENT / PENDING ORDERS (Orders tab) ---
  // Booking requests that haven't been completed (or declined) yet, for
  // ANY of this seller's listings — the aggregated, cross-listing version
  // of the per-listing "Bookings" modal opened from the Active Listings
  // table. Product sales have no "pending" state in this data model (a
  // live listing IS the pending-sale state), so this section is only
  // ever populated for service sellers with open booking requests.
  const [pendingOrders, setPendingOrders] = React.useState<any[]>([]);
  const [isLoadingPendingOrders, setIsLoadingPendingOrders] =
    React.useState(false);
  const hasLoadedPendingOrdersRef = React.useRef(false);

  const fetchPendingOrders = React.useCallback(async () => {
    setIsLoadingPendingOrders(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setPendingOrders([]);
        return;
      }

      const { data, error } = await supabase
        .from("bookings")
        .select("*")
        .eq("seller_id", user.id)
        .in("status", ["pending", "confirmed"])
        .order("booking_date", { ascending: true })
        .order("booking_time", { ascending: true });

      if (error) {
        console.error("[Orders] Failed to load pending orders:", error);
        setPendingOrders([]);
        return;
      }

      setPendingOrders(data || []);
    } catch (err) {
      console.error("Error loading pending orders:", err);
      setPendingOrders([]);
    } finally {
      setIsLoadingPendingOrders(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeTab !== "orders" || hasLoadedPendingOrdersRef.current) return;
    hasLoadedPendingOrdersRef.current = true;
    fetchPendingOrders();
  }, [activeTab, fetchPendingOrders]);

  React.useEffect(() => {
    const channel = supabase
      .channel("seller-pending-orders-tab-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "bookings" },
        () => {
          if (hasLoadedPendingOrdersRef.current) fetchPendingOrders();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchPendingOrders]);

  const openBookingsModal = async (listing: any) => {
    setActiveBookingsListing(listing);
    setIsBookingsModalOpen(true);
    setIsLoadingBookings(true);
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select("*")
        .eq("listing_id", listing.id)
        .order("booking_date", { ascending: true })
        .order("booking_time", { ascending: true });

      if (error) throw error;
      setBookings(data || []);
    } catch (err) {
      console.error("Error loading bookings:", err);
      setBookings([]);
    } finally {
      setIsLoadingBookings(false);
    }
  };

  const handleBookingStatusChange = async (
    booking: any,
    newStatus: "confirmed" | "declined" | "completed",
    listingOverride?: any
  ) => {
    // When called from the per-listing Bookings modal, activeBookingsListing
    // is already the right listing. When called from the aggregated Orders
    // tab (a single list spanning every listing), the caller passes the
    // matching listing explicitly instead.
    const listingForBooking = listingOverride || activeBookingsListing;
    try {
      const { error: updateError } = await supabase
        .from("bookings")
        .update({ status: newStatus })
        .eq("id", booking.id);

      if (updateError) throw updateError;

      // Completing a booking counts toward earnings the same way a product
      // sale does — reuse the existing "orders" table so the Total Earnings
      // metric picks it up automatically.
      if (newStatus === "completed") {
        const { error: orderError } = await supabase.from("orders").insert({
          listing_id: booking.listing_id,
          seller_id: booking.seller_id,
          amount: Number(listingForBooking?.price || 0),
        });
        if (orderError) throw orderError;

        // Slot-capped service listings (e.g. "3 slots for Rihanna Locs
        // this week") come down automatically once every slot has been
        // fulfilled — same end result as "Mark as Sold" for products, but
        // driven by completed bookings instead of a single sale. Listings
        // with no cap (slots_total is null/0) are left untouched.
        const slotsTotal = listingForBooking?.slots_total;
        if (slotsTotal) {
          const newSlotsCompleted =
            (listingForBooking?.slots_completed || 0) + 1;
          const isNowFull = newSlotsCompleted >= slotsTotal;

          const { error: slotUpdateError } = await supabase
            .from("listings")
            .update({
              slots_completed: newSlotsCompleted,
              ...(isNowFull ? { status: "completed" } : {}),
            })
            .eq("id", booking.listing_id);

          if (slotUpdateError) throw slotUpdateError;

          setActiveBookingsListing((prev: any) =>
            prev && prev.id === listingForBooking.id
              ? {
                  ...prev,
                  slots_completed: newSlotsCompleted,
                  status: isNowFull ? "completed" : prev.status,
                }
              : prev
          );
          // Keep the Home tab's listings state in step too, so the
          // Active Listings table doesn't need a full refetch to show
          // the new slot count / completed status.
          setListings((prev) =>
            prev.map((l: any) =>
              l.id === listingForBooking.id
                ? {
                    ...l,
                    slots_completed: newSlotsCompleted,
                    status: isNowFull ? "completed" : l.status,
                  }
                : l
            )
          );
        }
      }

      setBookings((prev) =>
        prev.map((b: any) => (b.id === booking.id ? { ...b, status: newStatus } : b))
      );
      // The aggregated Orders-tab list only ever shows pending/confirmed
      // bookings — declining or completing one removes it from view,
      // confirming one just updates its status in place.
      setPendingOrders((prev) =>
        newStatus === "confirmed"
          ? prev.map((b: any) =>
              b.id === booking.id ? { ...b, status: newStatus } : b
            )
          : prev.filter((b: any) => b.id !== booking.id)
      );
      fetchDashboardData();
    } catch (err: any) {
      console.error("Failed to update booking:", err);
      showAppToast({
        title: "Something went wrong",
        message: `Error updating booking: ${
          err.message || "Unexpected issue."
        }`,
      });
    }
  };

  // "Confirm" on a pending booking goes through the same confirm dialog
  // before actually accepting it — declining/completing stay one-click
  // since only accepting a booking was asked to get this guard.
  const requestConfirmBooking = (booking: any, listingOverride?: any) => {
    setConfirmDialog({
      isOpen: true,
      title: "Confirm booking?",
      message: `Confirm ${booking.buyer_name || "this buyer"}'s booking for ${
        booking.booking_date
      } at ${booking.booking_time}? They'll be notified it's accepted.`,
      confirmText: "Confirm",
      isDanger: false,
      onConfirm: () => {
        closeConfirmDialog();
        handleBookingStatusChange(booking, "confirmed", listingOverride);
      },
    });
  };

  // While the bookings modal is open, keep its list live — e.g. if a new
  // request comes in from a buyer while the seller is looking at this panel.
  React.useEffect(() => {
    if (!isBookingsModalOpen || !activeBookingsListing?.id) return;

    const channel = supabase
      .channel(`bookings-listing-${activeBookingsListing.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "bookings",
          filter: `listing_id=eq.${activeBookingsListing.id}`,
        },
        () => {
          openBookingsModal(activeBookingsListing);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isBookingsModalOpen, activeBookingsListing?.id]);

  // Clean up object URLs on unmount or preview update
  React.useEffect(() => {
    return () => {
      imagePreviews.forEach((url: any) => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const filesArray = Array.from(e.target.files);

    if (filesArray.length < 3 || filesArray.length > 6) {
      showAppToast({
        title: "Heads up",
        message:
          "Please upload between 3 and 6 high-quality images of your item.",
      });
      e.target.value = "";
      return;
    }

    setSelectedImages(filesArray);
    const previews = filesArray.map((file: any) => URL.createObjectURL(file));
    setImagePreviews(previews);
    // Every new preview starts "loading" until its own <img> onLoad fires —
    // reset this alongside the previews themselves so a fresh pick doesn't
    // inherit stale loaded/unloaded flags from a previous selection.
    setLoadedPreviews(filesArray.map(() => false));
  };

  const markPreviewLoaded = (index: number) => {
    setLoadedPreviews((prev) => {
      if (prev[index]) return prev;
      const next = [...prev];
      next[index] = true;
      return next;
    });
  };

  // Lets a seller remove a single picked photo (the "X" on each thumbnail)
  // without starting the whole picker over — keeps selectedImages,
  // imagePreviews, and loadedPreviews all in sync by index, and revokes
  // the removed preview's object URL since it's no longer needed.
  const handleRemoveImage = (index: number) => {
    setImagePreviews((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== index);
    });
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setLoadedPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const allPreviewsLoaded =
    imagePreviews.length > 0 && loadedPreviews.every(Boolean);

  const fetchDashboardData = React.useCallback(async () => {
    try {
      setIsLoadingDashboard(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data: listingsData, error: listingsError } = await supabase
        .from("listings")
        .select("*")
        .eq("seller_id", user.id)
        .order("created_at", { ascending: false });

      if (listingsError) throw listingsError;
      const fetchedListings = listingsData || [];

      // Self-cleaning 24h expiry for open services like laundry: there's no
      // server-side scheduled job enforcing this, so instead we check on
      // every dashboard load whether this seller has any of their own
      // still-"active" open-24h listings older than 24h, and flip them to
      // "expired" right here. The buyer feed never actually needs this DB
      // write to hide an expired listing (it filters by age client-side on
      // its own), but this keeps the seller's own dashboard status honest
      // and keeps the DB consistent for anyone else querying it directly.
      const nowMs = Date.now();
      const newlyExpiredIds = fetchedListings
        .filter(
          (item: any) =>
            item.status === "active" &&
            item.auto_expires_24h &&
            item.created_at &&
            nowMs - new Date(item.created_at).getTime() > 24 * 60 * 60 * 1000
        )
        .map((item: any) => item.id);

      if (newlyExpiredIds.length > 0) {
        const { error: expireError } = await supabase
          .from("listings")
          .update({ status: "expired" })
          .in("id", newlyExpiredIds);

        if (expireError) {
          console.error("Failed to expire stale listings:", expireError);
        } else {
          fetchedListings.forEach((item: any) => {
            if (newlyExpiredIds.includes(item.id)) item.status = "expired";
          });
        }
      }

      setListings(fetchedListings);

      const activeListings = fetchedListings.filter(
        (item: any) => item.status === "active"
      );

      const stockTotal = activeListings.reduce((acc: number, item: any) => {
        if (item.listing_kind === "service") {
          // Slot-capped service listing (e.g. "4 slots for Rihanna Locs
          // this week" at KES 600/slot): potential value is price ×
          // slots still unbooked — mirrors "price × units left" for
          // physical stock below, and shrinks as slots_completed rises
          // via "Mark as Completed" bookings.
          if (item.slots_total) {
            const remainingSlots = Math.max(
              0,
              Number(item.slots_total) - Number(item.slots_completed || 0)
            );
            return acc + Number(item.price || 0) * remainingSlots;
          }
          // Open/no-cap service listing (e.g. laundry, or any service
          // the seller left uncapped) — there's no bounded number of
          // future bookings to project, so it can't contribute a
          // number here. Whether that means the whole metric should
          // read "N/A" is decided below, once every active listing has
          // been looked at.
          return acc;
        }
        // Physical product path — unchanged. For listings tracking real
        // inventory (LIVE NOW products with stock_quantity set),
        // potential value is price × units left. Everything else
        // (pre-order, older listings without stock_quantity) falls back
        // to just the listing price.
        const units =
          item.stock_quantity != null ? Number(item.stock_quantity) : 1;
        return acc + Number(item.price || 0) * units;
      }, 0);

      // "N/A" only when there's at least one active listing AND none of
      // them are actually computable (every one is an open/uncapped
      // service) — a seller with zero active listings still correctly
      // sees KES 0, matching the pre-existing empty-state behavior.
      const hasComputableListing = activeListings.some(
        (item: any) => item.listing_kind !== "service" || item.slots_total
      );
      setPotentialShopValue(
        activeListings.length > 0 && !hasComputableListing ? null : stockTotal
      );

      const { data: ordersData, error: ordersError } = await supabase
        .from("orders")
        .select("amount")
        .eq("seller_id", user.id);

      if (ordersError) throw ordersError;

      const salesTotal = (ordersData || []).reduce(
  (acc: number, order: any) => acc + Number(order.amount || 0),
  0
);
      setTotalValueGenerated(salesTotal);

      // Pending booking counts per listing, for the "Bookings" action badge.
      const { data: pendingBookings, error: bookingsError } = await supabase
        .from("bookings")
        .select("listing_id")
        .eq("seller_id", user.id)
        .eq("status", "pending");

      if (bookingsError) throw bookingsError;

      const counts: Record<string, number> = {};
      (pendingBookings || []).forEach((b: any) => {
        counts[b.listing_id] = (counts[b.listing_id] || 0) + 1;
      });
      setPendingBookingsByListing(counts);
    } catch (err) {
      console.error("Error loading dashboard data:", err);
    } finally {
      setIsLoadingDashboard(false);
      setInitialListingsLoaded(true);
    }
  }, []);

  React.useEffect(() => {
    fetchDashboardData();

    const channel = supabase
      .channel("seller-dashboard-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "listings" },
        () => {
          fetchDashboardData();
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders" },
        () => {
          fetchDashboardData();
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "bookings" },
        () => {
          fetchDashboardData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchDashboardData]);

  React.useEffect(() => {
    async function fetchStoreInfo() {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        let res: any = await supabase
          .from("seller_applications")
          .select("store_code, university, location, county")
          .eq("id", user.id)
          .maybeSingle();
        if (res.error) {
          // store_code column not added yet — still show the location, and
          // fall back to a link built from the seller id.
          res = await supabase
            .from("seller_applications")
            .select("university, location, county")
            .eq("id", user.id)
            .maybeSingle();
        }
        const row = res.data;
        // Only the random store code is ever used for a link — never the
        // account id.
        setStoreCode(row?.store_code || "");
        setStoreLocationLine(formatStoreLocation(row));
      } catch (err) {
        console.error("Error loading store info:", err);
      } finally {
        setStoreInfoLoaded(true);
      }
    }
    fetchStoreInfo();
  }, []);

  React.useEffect(() => {
    async function fetchSellerProfile() {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase
          .from("seller_applications")
          .select(
            "display_name, phone_number, whatsapp_number, avatar_id, listing_type, category"
          )
          .eq("id", user.id)
          .maybeSingle();

        if (data && !error) {
          const loadedListingType =
            (data.listing_type || "").toLowerCase() === "service"
              ? "service"
              : "product";
          setSellerListingType(loadedListingType);
          const resolvedThemeFamily = getThemeFamilyForCategory(data.category);
          setThemeFamily(resolvedThemeFamily);
          writeOwnedCache("seller_theme_family", user.id, resolvedThemeFamily);
          setSellerParentCategory(getParentCategoryForRaw(data.category));
          // Only a real, non-empty value already saved in the DB counts as
          // "locked" — a localStorage fallback or empty value does NOT.
          const dbDisplayName = data.display_name?.trim() || "";
          const alreadyLocked = dbDisplayName.length > 0;

          const loadedDisplayName = alreadyLocked
            ? dbDisplayName
            : readOwnedCache("seller_display_name", user.id) || "";
          let loadedPhone = data.phone_number || "";
          if (!loadedPhone) {
            // The application phone is private, so it comes from a function
            // that only ever returns the signed-in seller's own row.
            const { data: privateApp } = await supabase.rpc(
              "get_my_private_application"
            );
            loadedPhone = (privateApp as any)?.phone || "";
          }
          const loadedWhatsapp = data.whatsapp_number || loadedPhone || "";
          const loadedAvatar = data.avatar_id || "avatar_1";

          setSellerName(loadedDisplayName);
          setPhone(normalizeLoadedPhone(loadedPhone));
          setWhatsapp(normalizeLoadedPhone(loadedWhatsapp));
          setAvatarId(loadedAvatar);
          setIsDisplayNameLocked(alreadyLocked);

          setOriginalDbData({
            display_name: dbDisplayName,
            phone_number: loadedPhone,
            whatsapp_number: loadedWhatsapp,
            avatar_id: loadedAvatar,
          });
        }
      } catch (err) {
        console.error("Error loading seller profile:", err);
      } finally {
        setInitialProfileLoaded(true);
      }
    }

    fetchSellerProfile();
  }, []);

  const isFormUnchanged =
    sellerName === originalDbData.display_name &&
    phone === (originalDbData.phone_number || PHONE_PREFIX) &&
    whatsapp === (originalDbData.whatsapp_number || PHONE_PREFIX) &&
    avatarId === originalDbData.avatar_id;

  // Only meaningful once the seller has picked a subcategory — see the
  // HOME_SERVICES_* config above for what each of these controls.
  const isHomeServicesCategory = sellerParentCategory?.id === "home_services";
  const isOpen24hSubcategory =
    isHomeServicesCategory &&
    HOME_SERVICES_OPEN_24H_SUBCATEGORIES.has(selectedSubcategory);
  const isSlotRequiredSubcategory =
    isHomeServicesCategory &&
    HOME_SERVICES_SLOT_REQUIRED_SUBCATEGORIES.has(selectedSubcategory);
  // laundry is the only Home Services subcategory with no slots concept —
  // it's open/walk-in and relies on the 24h auto-expiry instead.
  const hidesCapacityField =
    isHomeServicesCategory && !isSlotRequiredSubcategory;

  const handleMarkAsSold = async (
    listingId: string,
    price: number,
    currentStockQuantity?: number | null
  ) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        showAppToast({
          title: "Heads up",
          message: "Authentication error: Please sign in again.",
        });
        return;
      }

      // Listings with multiple units on hand: selling one unit decrements
      // the count instead of closing the whole listing. Only once stock
      // hits 0 does the listing actually flip to "sold" and drop off the
      // buyer feed. Listings without stock_quantity set (older listings,
      // or anything not created as LIVE NOW) keep the original one-shot
      // behavior.
      const hasStock =
        currentStockQuantity !== null && currentStockQuantity !== undefined;
      const nextStockQuantity = hasStock
        ? Math.max(0, currentStockQuantity! - 1)
        : null;
      const isNowFullySold = hasStock ? nextStockQuantity === 0 : true;

      const listingUpdate: Record<string, any> = isNowFullySold
        ? { status: "sold", ...(hasStock ? { stock_quantity: 0 } : {}) }
        : { stock_quantity: nextStockQuantity };

      setListings((prev) =>
        prev.map((item: any) =>
          item.id === listingId ? { ...item, ...listingUpdate } : item
        )
      );

      const { error: updateError } = await supabase
        .from("listings")
        .update(listingUpdate)
        .eq("id", listingId)
        .eq("seller_id", user.id);

      if (updateError) throw updateError;

      const { error: orderError } = await supabase.from("orders").insert({
        listing_id: listingId,
        seller_id: user.id,
        amount: price,
      });

      if (orderError) throw orderError;

      await fetchDashboardData();
    } catch (err: any) {
      console.error("Failed to mark listing as sold:", err);
      showAppToast({
        title: "Something went wrong",
        message: `Error updating item status: ${
          err.message || "Unexpected issue."
        }`,
      });
      fetchDashboardData();
    }
  };

  // Pulls the storage path back out of a public URL like:
  // https://xxxx.supabase.co/storage/v1/object/public/listings-gallery/<path>
  const getStoragePathFromPublicUrl = (url: string): string | null => {
    const marker = "/listings-gallery/";
    const idx = url.indexOf(marker);
    if (idx === -1) return null;
    return url.substring(idx + marker.length);
  };

  const handleDeleteListing = async (
    listingId: string,
    listingTitle: string
  ) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        showAppToast({
          title: "Heads up",
          message: "Authentication error: Please sign in again.",
        });
        return;
      }

      // Guard: block delete if this listing has ANY bookings or sale
      // history, even a single completed order on a multi-stock listing —
      // this protects buyer/seller records from being silently orphaned.
      const { count: bookingsCount, error: bookingsErr } = await supabase
        .from("bookings")
        .select("id", { count: "exact", head: true })
        .eq("listing_id", listingId);
      if (bookingsErr) throw bookingsErr;

      const { count: ordersCount, error: ordersErr } = await supabase
        .from("orders")
        .select("id", { count: "exact", head: true })
        .eq("listing_id", listingId);
      if (ordersErr) throw ordersErr;

      if ((bookingsCount || 0) > 0 || (ordersCount || 0) > 0) {
        showAppToast({
          title: "Can't delete this listing",
          message:
            "This listing has bookings or sales tied to it, so it can't be deleted. This protects your records — you can leave it as-is instead.",
        });
        return;
      }

      // Grab photo_urls before deleting the row, so we can clean up
      // storage after the row is gone.
      const listingToDelete = listings.find((item: any) => item.id === listingId);

      const { error: deleteError } = await supabase
        .from("listings")
        .delete()
        .eq("id", listingId)
        .eq("seller_id", user.id);

      if (deleteError) throw deleteError;

      setListings((prev) => prev.filter((item: any) => item.id !== listingId));

      // Keep Potential Shop Value in sync: only active listings contribute
      // to that total (see the same "active" filter used when it's first
      // calculated on load), so only subtract if the deleted listing was
      // actually counted in it. Same price × units-left / price ×
      // remaining-slots logic as that original calculation. If the metric
      // is currently "N/A" (prev === null), the deleted listing wasn't
      // counted either way, so there's nothing to adjust.
      if (
        listingToDelete &&
        listingToDelete.status === "active" &&
        potentialShopValue !== null
      ) {
        let removedValue = 0;
        if (listingToDelete.listing_kind === "service") {
          if (listingToDelete.slots_total) {
            const remainingSlots = Math.max(
              0,
              Number(listingToDelete.slots_total) -
                Number(listingToDelete.slots_completed || 0)
            );
            removedValue = Number(listingToDelete.price || 0) * remainingSlots;
          }
          // Open/no-cap service listings were never counted in the total
          // in the first place, so removedValue correctly stays 0.
        } else {
          const units =
            listingToDelete.stock_quantity != null
              ? Number(listingToDelete.stock_quantity)
              : 1;
          removedValue = Number(listingToDelete.price || 0) * units;
        }
        setPotentialShopValue((prev) =>
          prev === null ? prev : Math.max(0, prev - removedValue)
        );
      }

      // Best-effort image cleanup — the listing is already gone either
      // way, so a storage failure here shouldn't block or alarm the seller.
      const photoUrls: string[] = listingToDelete?.photo_urls || [];
      const storagePaths = photoUrls
        .map(getStoragePathFromPublicUrl)
        .filter((p): p is string => !!p);

      if (storagePaths.length > 0) {
        const { error: storageError } = await supabase.storage
          .from("listings-gallery")
          .remove(storagePaths);
        if (storageError) {
          console.error("Failed to clean up listing images:", storageError);
        }
      }
    } catch (err: any) {
      console.error("Failed to delete listing:", err);
      showAppToast({
        title: "Something went wrong",
        message: `Error deleting listing: ${
          err.message || "Unexpected issue."
        }`,
      });
      fetchDashboardData();
    }
  };

  // Entry point every "Delete" button should call — opens the confirm
  // dialog first; the actual delete only runs from its onConfirm.
  const requestDeleteListing = (listingId: string, listingTitle: string) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete listing?",
      message: `Delete "${listingTitle}"? This can't be undone.`,
      confirmText: "Delete",
      isDanger: true,
      onConfirm: () => {
        closeConfirmDialog();
        handleDeleteListing(listingId, listingTitle);
      },
    });
  };

  // Real sign-out. (The "Exit" button up top only returns to the marketplace
  // and leaves the person signed in.) "local" ends the session on this
  // device; "global" ends it on every device the account is signed in on.
  const requestSignOut = (scope: "local" | "global") => {
    const everywhere = scope === "global";
    setConfirmDialog({
      isOpen: true,
      title: everywhere ? "Log out of all devices?" : "Log out?",
      message: everywhere
        ? "This signs your account out everywhere it is logged in, including this device. Other devices may stay signed in for up to an hour."
        : "You'll need to sign in again to reach your dashboard.",
      confirmText: everywhere ? "Log out everywhere" : "Log out",
      isDanger: true,
      onConfirm: async () => {
        closeConfirmDialog();
        const { error } = await supabase.auth.signOut({ scope });
        if (error) {
          showAppToast({
            title: "Could not log out",
            message: "Check your connection and try again.",
          });
          return;
        }
        try {
          localStorage.removeItem("current_buyer_screen");
        } catch {}
        navigateTo("entry");
      },
    });
  };

  const totalViews = listings.reduce(
    (sum, item) => sum + Number(item.views || 0),
    0
  );

  const selectedAvatarObj =
    AVATAR_PRESETS.find((a: any) => a.id === avatarId) || AVATAR_PRESETS[0];

  // Concrete hex values (not CSS vars) for the seller's category color —
  // the Create Listing Stepper needs real hex since Framer Motion tweens
  // its step-indicator colors numerically and can't resolve var(--t-500)
  // at animation time. Same palette the rest of the dashboard themes off.
  const stepperPalette =
    TAILWIND_PALETTE[themeFamily] || TAILWIND_PALETTE.orange;
  const stepperAccentColor = stepperPalette[500];
  const stepperAccentColorHover = stepperPalette[600];

  // Tracks which of the 4 Create Listing steps is active, purely so the
  // stepper's own "Next"/"Complete" button can be swapped from ordinary
  // step-advance to the real submit-and-validate flow only once the seller
  // reaches the last step (Review & Publish) — same 4-step flow for both
  // products and services.
  const [createListingStep, setCreateListingStep] = React.useState(1);
  const CREATE_LISTING_TOTAL_STEPS = 4;

  // Extracted from the old <form onSubmit>. Runs the full validation +
  // upload + insert flow — now triggered from the stepper's final step
  // rather than native form submission, since the stepper spans 4 steps
  // rather than one long scrolling page.
  const handleCreateListingSubmit = async () => {
    const bannedWord = findBannedDescriptionKeyword(productDescription);
    if (bannedWord) {
      showAppToast({
        title: "Description needs an edit",
        message: `Please remove pricing language from your description ("${bannedWord}"). Prices should only be set in the listing price field, not written in the description.`,
      });
      return;
    }

    // Build up the exact list of what's missing, field by field, rather
    // than one generic message — so the seller knows precisely what to go
    // back and fix instead of having to hunt across all 4 steps.
    const missingFields: string[] = [];

    if (!selectedSubcategory) missingFields.push("Subcategory");
    if (!productName.trim())
      missingFields.push(
        sellerListingType === "service" ? "Service Name" : "Product Name"
      );
    if (!productPrice || Number(productPrice) <= 0)
      missingFields.push(
        sellerListingType === "service" ? "Starting Price" : "Price"
      );
    if (!productDescription.trim()) missingFields.push("Description");

    if (selectedImages.length < 3 || selectedImages.length > 6) {
      missingFields.push("Photos (3-6 required)");
    } else if (!allPreviewsLoaded) {
      // Images are picked but a thumbnail is still decoding — treat this
      // the same as "not ready yet" so we never publish before every
      // photo has actually finished loading.
      missingFields.push("Photos (still loading — wait a moment)");
    }

    if (sellerListingType === "service") {
      if (!serviceArea.trim()) missingFields.push("Service Area");
      if (availDays.length === 0) missingFields.push("Availability Days");
      if (!availStart) missingFields.push("Availability Start Time");
      if (!availEnd) missingFields.push("Availability End Time");
      if (isSlotRequiredSubcategory && !capacitySlots)
        missingFields.push("Total Slots Available");
    } else {
      if (listingType === "PRE_ORDER" && !preorderUnits) {
        missingFields.push("Target Capacity (Units)");
      }
      if (listingType === "LIVE" && !unitsToSell) {
        missingFields.push("Units To Sell");
      }
    }

    if (missingFields.length > 0) {
      showAppToast({
        title: "A few details are missing",
        message: `Please go back and fill in: ${missingFields.join(", ")}.`,
      });
      return;
    }

    setIsUploading(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        showAppToast({
          title: "Heads up",
          message: "Authentication error: Please log back in.",
        });
        return;
      }

      // 1. Upload images to storage
      const uploadPromises = selectedImages.map(async (file) => {
        const fileExtension = file.name.split(".").pop();
        const uniqueFileName = `${user.id}/${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 7)}.${fileExtension}`;

        const { error: uploadError } = await supabase.storage
          .from("listings-gallery")
          .upload(uniqueFileName, file, {
            cacheControl: "3600",
            upsert: false,
          });

        if (uploadError)
          throw new Error(`Image upload failed: ${uploadError.message}`);

        const {
          data: { publicUrl },
        } = supabase.storage
          .from("listings-gallery")
          .getPublicUrl(uniqueFileName);

        return publicUrl;
      });

      const uploadedUrls = await Promise.all(uploadPromises);

      // 2. Insert listing into DB
      const basePayload = {
        seller_id: user.id,
        title: productName,
        price: parseFloat(productPrice),
        description: productDescription,
        photo_urls: uploadedUrls,
        status: "active",
        listing_kind: sellerListingType,
        subcategory: selectedSubcategory,
      };

      const typeSpecificPayload =
        sellerListingType === "service"
          ? {
              service_area: serviceArea,
              turnaround: turnaround || null,
              availability: {
                days: availDays,
                start_time: availStart,
                end_time: availEnd,
                slot_minutes: slotMinutes,
              },
              // null/0 means "no cap" — the listing stays up
              // indefinitely until the seller removes it
              // manually. A positive number caps how many
              // completed bookings this listing can serve
              // before it auto-hides from buyers.
              //
              // Home Services subcategories override this:
              // slot-required ones (home_cleaning, pet_sitting,
              // moving_help, errand_running, baby_sitting)
              // force a real number here; laundry forces null
              // since it's the only one with no slots concept.
              slots_total: isSlotRequiredSubcategory
                ? parseInt(capacitySlots, 10)
                : hidesCapacityField
                ? null
                : capacitySlots
                ? parseInt(capacitySlots, 10)
                : null,
              slots_completed: 0,
              // Only ever set for "laundry" today — the buyer
              // feed and this dashboard both hide any listing
              // whose subcategory is in
              // HOME_SERVICES_OPEN_24H_SUBCATEGORIES once 24h
              // have passed since created_at, so this flag is
              // just a fast, explicit signal of that intent
              // (rather than something either side has to
              // re-derive from the subcategory string alone).
              auto_expires_24h: isOpen24hSubcategory,
            }
          : {
              execution_type: listingType,
              target_capacity:
                listingType === "PRE_ORDER"
                  ? parseInt(preorderUnits, 10)
                  : null,
              // Only set for LIVE NOW — PRE_ORDER tracks its
              // own count via target_capacity/slots instead.
              stock_quantity:
                listingType === "LIVE" ? parseInt(unitsToSell, 10) : null,
            };

      const { error: dbError } = await supabase
        .from("listings")
        .insert({ ...basePayload, ...typeSpecificPayload });

      if (dbError) throw dbError;

      setIsSuccessScreen(true);
      setProductName("");
      setProductPrice("");
      setProductDescription("");
      setSelectedSubcategory("");
      setSelectedImages([]);
      setImagePreviews([]);
      setLoadedPreviews([]);
      setServiceArea("");
      setTurnaround("");
      setAvailDays(["Mon", "Tue", "Wed", "Thu", "Fri"]);
      setAvailStart("09:00");
      setAvailEnd("17:00");
      setSlotMinutes(60);
      setCapacitySlots("");
      setPreorderUnits("5");
      setUnitsToSell("1");
      setCreateListingStep(1);
    } catch (error: any) {
      console.error("Listing creation failed:", error);
      showAppToast({
        title: "Something went wrong",
        message:
          error.message ||
          "An unexpected issue occurred while saving your listing.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      style={themeVars}
      className="min-h-screen bg-gradient-to-br from-black via-[var(--t-950)] to-black text-white p-6 pb-28 relative overflow-hidden font-sans"
    >
      {/* APP TOAST — replaces native alert() popups app-wide. Floats
          independently, auto-dismisses like a WhatsApp notification. */}
      {appToast && (
        <div className="fixed top-5 right-5 left-5 sm:left-auto sm:w-96 z-[200] animate-toastSlideIn">
          <style>{`
            @keyframes toastSlideIn {
              from { transform: translateY(-16px); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
            .animate-toastSlideIn { animation: toastSlideIn 0.3s ease-out; }
            @keyframes toastShrink {
              from { width: 100%; }
              to { width: 0%; }
            }
            .animate-toastShrink { animation: toastShrink 6s linear forwards; }
          `}</style>
          {(() => {
            const isSuccess = appToast.variant === "success";
            const isInfo = appToast.variant === "info";
            const accent = isSuccess ? "#2ECC71" : isInfo ? "#FFB800" : "#FF3B5C";
            const accentHover = isSuccess
              ? "#58d98a"
              : isInfo
              ? "#ffc933"
              : "#ff6b84";
            return (
              <div
                className="bg-[#161616] rounded-2xl shadow-2xl shadow-black/50 overflow-hidden"
                style={{ border: `1px solid ${accent}4d` }}
              >
                <div className="flex items-start gap-3 p-4">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${accent}26` }}
                  >
                    {isSuccess ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ color: accent }}
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ color: accent }}
                      >
                        <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white">
                      {appToast.title}
                    </p>
                    <p className="text-xs text-white/70 mt-0.5 leading-snug">
                      {appToast.message}
                    </p>
                    {appToast.action && (
                      <button
                        onClick={() => {
                          setAppToast(null);
                          appToast.action?.onClick();
                        }}
                        className="text-xs font-semibold mt-2"
                        style={{ color: accent }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = accentHover)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = accent)
                        }
                      >
                        {appToast.action.label}
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => setAppToast(null)}
                    className="text-white/40 hover:text-white/70 flex-shrink-0"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                <div className="h-0.5 bg-white/10">
                  <div
                    className="h-full animate-toastShrink"
                    style={{
                      backgroundColor: accent,
                      animationDuration: `${appToast.durationMs ?? 6000}ms`,
                    }}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      )}

      <div className="absolute -top-40 -left-40 w-96 h-96 bg-[var(--t-700)] rounded-full blur-[120px] opacity-30 pointer-events-none" />
      <div className="absolute top-1/2 -right-40 w-96 h-96 bg-[var(--t-600)] rounded-full blur-[150px] opacity-25 pointer-events-none" />

      <div className="w-full px-4 sm:px-8 lg:px-12 relative z-10">
        {/* TOP BAR — slim and persistent across every tab (Exit, Create
            New Listing). The avatar/display name shows here on every tab
            EXCEPT Home — Home already gets "Hello, {name}" as its own
            hero heading just below, with the avatar moved down to sit on
            that same line, so repeating name+avatar up here again would
            be redundant only on that one tab. Orders/Account have no
            such heading of their own, so the name here is their only
            "whose dashboard is this" cue — hence the larger type. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 border-b border-[var(--t-900-40)] pb-5">
          {activeTab === "home" ? (
            <div />
          ) : (
            <div className="flex items-center gap-2.5">
              <div
                className={`w-9 h-9 rounded-full ${selectedAvatarObj.color} flex items-center justify-center text-sm font-bold text-white shadow-lg shrink-0 border border-white/20`}
              >
                {sellerName.charAt(0).toUpperCase() || "S"}
              </div>
              <p className="text-lg font-semibold text-[var(--t-200)]">
                {sellerName}
              </p>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => {
                const hex = (TAILWIND_PALETTE[themeFamily] ||
                  TAILWIND_PALETTE.orange)[500];
                if (onRequestHomeTransition) {
                  onRequestHomeTransition(hex);
                } else {
                  navigateTo("entry");
                }
              }}
              className="flex items-center gap-2 border border-[var(--t-200-20)] bg-[var(--t-950-20)] hover:bg-[var(--t-950-40)] text-[var(--t-200)] px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
            >
              <ArrowLeft size={18} />
              Exit
            </button>

            <button
              ref={homeCreateListingBtnRef}
              onClick={() => {
                if (!isDisplayNameLocked) {
                  triggerContactCardToast();
                  return;
                }
                setIsSuccessScreen(false);
                setIsCreateModalOpen(true);
              }}
              className="bg-white text-[#1c0d02] font-semibold py-2.5 px-5 rounded-xl shadow-lg shadow-[var(--t-950-50)] hover:bg-[var(--t-50)] transition-all duration-200 flex items-center justify-center gap-2 text-sm"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Create New Listing
            </button>
          </div>
        </div>

        {/* TAB NAVIGATION — Home / Orders / Account now lives exclusively
            in the fixed bottom bar (see the end of this component), on
            every screen size, matching buyer-home. There used to be a
            second horizontal row of these same tabs here for tablet/
            desktop, which is why the top bar above could never fully
            get rid of the display name — removed in favor of one tab
            bar everyone shares. */}

        {activeTab === "home" && (
          <>
            {/* HOME TAB HEADER — the full hero, now scoped to just this tab.
                Avatar sits inline with the heading itself (rather than up
                in the top bar, which is blank on this tab) since "Hello,
                {name}" already says who this is — no need to say it twice
                on the same screen. */}
            <div className="mb-8" ref={homeDisplayNameRef}>
              <div className="flex items-center gap-3">
                <div
                  className={`w-9 h-9 rounded-full ${selectedAvatarObj.color} flex items-center justify-center text-sm font-bold text-white shadow-lg shrink-0 border border-white/20`}
                >
                  {sellerName.charAt(0).toUpperCase() || "S"}
                </div>
                <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-white via-[var(--t-200)] to-[var(--t-400)] bg-clip-text text-transparent">
                  Hello, {sellerName}
                </h1>
              </div>
              <p className="text-[var(--t-200-60)] text-sm mt-0.5">
                Manage your shop front, track peer connections, and update
                listings.
              </p>
            </div>
            {/* METRICS GRID */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div
                ref={homePotentialValueRef}
                className="bg-[var(--t-950-20)] border border-[var(--t-900-30)] backdrop-blur-xl rounded-2xl p-5 shadow-inner"
              >
                <p className="text-[var(--t-200-50)] text-xs font-medium uppercase tracking-wider">
                  {sellerListingType === "service"
                    ? "Potential Earnings"
                    : "Potential Shop Value"}
                </p>
                {potentialShopValue === null ? (
                  <p className="text-2xl font-bold mt-2 text-[var(--t-400)]">
                    N/A
                  </p>
                ) : (
                  <SpeedingText
                    value={potentialShopValue}
                    prefix="KES "
                    duration={900}
                    maxBlur={6}
                    blurStrength={1}
                    startOnView={false}
                    width="auto"
                    height="auto"
                    align="left"
                    backgroundColor="transparent"
                    textClassName="text-2xl font-bold mt-2 text-[var(--t-400)]"
                  />
                )}
                <p className="text-xs text-[var(--t-200-40)] mt-1">
                  {potentialShopValue === null
                    ? "Open listing with no slot cap — no fixed potential to project"
                    : sellerListingType === "service"
                    ? "Value of all active service listings"
                    : "Value of all current live listings"}
                </p>
              </div>

              <div
                ref={homeActiveListingsRef}
                className="bg-[var(--t-950-20)] border border-[var(--t-900-30)] backdrop-blur-xl rounded-2xl p-5 shadow-inner"
              >
                <p className="text-[var(--t-200-50)] text-xs font-medium uppercase tracking-wider">
                  Active Listings
                </p>
                <h3 className="text-2xl font-bold mt-2">
                  {listings.filter((item: any) => item.status === "active").length}{" "}
                  Items
                </h3>
                <p className="text-xs text-[var(--t-200-40)] mt-1">
                  Live and visible to buyers
                </p>
              </div>

              <div
                ref={homeTotalEarningsRef}
                className="bg-[var(--t-950-20)] border border-[var(--t-900-30)] backdrop-blur-xl rounded-2xl p-5 shadow-inner"
              >
                <p className="text-[var(--t-200-50)] text-xs font-medium uppercase tracking-wider">
                  Total Earnings
                </p>
                <SpeedingText
                  value={totalValueGenerated}
                  prefix="KES "
                  duration={900}
                  maxBlur={6}
                  blurStrength={1}
                  startOnView={false}
                  width="auto"
                  height="auto"
                  align="left"
                  backgroundColor="transparent"
                  textClassName={`text-2xl font-bold mt-2 ${
                    themeFamily === "green"
                      ? "text-yellow-400"
                      : "text-green-400"
                  }`}
                />
                <p className="text-xs text-[var(--t-200-40)] mt-1">
                  {sellerListingType === "service"
                    ? "Accumulated from completed bookings"
                    : "Accumulated from items marked 'SOLD'"}
                </p>
              </div>

              <div
                ref={homeTotalClicksRef}
                className="bg-[var(--t-950-20)] border border-[var(--t-900-30)] backdrop-blur-xl rounded-2xl p-5 shadow-inner"
              >
                <p className="text-[var(--t-200-50)] text-xs font-medium uppercase tracking-wider">
                  Total Item Clicks
                </p>
                <SpeedingText
                  value={totalViews}
                  duration={900}
                  maxBlur={6}
                  blurStrength={1}
                  startOnView={false}
                  width="auto"
                  height="auto"
                  align="left"
                  backgroundColor="transparent"
                  textClassName="text-2xl font-bold mt-2 text-[var(--t-300)]"
                />
                <p className="text-xs text-[var(--t-200-40)] mt-1">
                  Times detail modal opened
                </p>
              </div>
            </div>

            {/* BUYERS ARE ASKING FOR — demand from buyers' "Make a request"
                in this seller's category. Hidden until at least one
                subcategory reaches the 5-request threshold. */}
            {demand.length > 0 && (
              <div className="mb-8 rounded-2xl border border-[var(--t-800-40)] bg-[var(--t-950-20)] p-6 shadow-xl">
                <h2 className="text-xl font-bold text-white mb-1">
                  Buyers are asking for…
                </h2>
                <p className="text-xs text-[var(--t-200-60)] leading-relaxed mb-4">
                  Requests from buyers in{" "}
                  {sellerParentCategory?.name || "your category"}. Buyers'
                  names are never shown.
                </p>

                <div className="flex flex-col gap-3">
                  {demand.slice(0, 5).map((row: any) => {
                    const subName =
                      (sellerParentCategory?.subcategories || []).find(
                        (s: any) => s.id === row.subcategory
                      )?.name || "General";
                    const counties: string[] = row.top_counties || [];
                    const samples: string[] = (row.samples || []).slice(0, 2);
                    return (
                      <div
                        key={row.subcategory}
                        className="rounded-xl border border-[var(--t-900-40)] bg-black/30 p-4"
                      >
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="text-sm font-semibold text-white">
                            {subName}
                          </p>
                          <span className="shrink-0 text-xs font-bold text-[var(--t-200)]">
                            {row.request_count} buyers asked
                          </span>
                        </div>
                        {counties.length > 0 && (
                          <p className="text-[11px] text-[var(--t-200-60)] mt-1">
                            Mostly in {counties.join(", ")}
                          </p>
                        )}
                        {samples.map((text: string, i: number) => (
                          <p
                            key={i}
                            className="text-xs italic text-[var(--t-200-80)] mt-2 line-clamp-2"
                          >
                            “{text}”
                          </p>
                        ))}
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (!isDisplayNameLocked) {
                      triggerContactCardToast();
                      return;
                    }
                    setIsSuccessScreen(false);
                    setIsCreateModalOpen(true);
                  }}
                  className="mt-4 bg-white text-[#1c0d02] font-semibold py-2.5 px-5 rounded-xl shadow-lg hover:bg-[var(--t-50)] transition-all duration-200 text-sm"
                >
                  Create a listing
                </button>
              </div>
            )}

            {/* ACTIVE LISTINGS TABLE AREA */}
            <div className="bg-[var(--t-950-10)] border border-[var(--t-900-30)] backdrop-blur-md rounded-2xl p-6 shadow-xl">
              <h2 className="text-lg font-semibold mb-4 tracking-wide text-[var(--t-100)]">
                Your Active Listings
              </h2>

              {isLoadingDashboard ? (
                <div className="py-8 text-center">
                  <Loader message="Loading listings" scale={0.4} />
                </div>
              ) : listings.length === 0 ? (
                <div className="py-8 text-center text-stone-400">
                  No listings found. Create your first listing to get started!
                </div>
              ) : (
                <>
                  {/* MOBILE: stacked cards. The table below is unusable on
                  narrow screens (four columns squeezed into one row makes
                  badges/buttons overlap), so under the md breakpoint we
                  render each listing as its own vertically-stacked card
                  instead. */}
                  <div className="md:hidden divide-y divide-[var(--t-900-20)]">
                    {listings.map((item: any) => (
                      <div key={item.id} className="py-4 flex flex-col gap-3">
                        <div className="flex items-start gap-3">
                          {item.photo_urls?.[0] ? (
                            <img
                              src={item.photo_urls[0]}
                              alt={item.title}
                              className="w-12 h-12 rounded-lg object-cover border border-[var(--t-900-30)] shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-[var(--t-950-50)] border border-[var(--t-900-30)] flex items-center justify-center text-[var(--t-400)] shrink-0">
                              📦
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <span className="font-medium text-white block break-words">
                              {item.title}
                            </span>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[var(--t-950)] text-[var(--t-400)] border border-[var(--t-800-30)]">
                                {item.status}
                              </span>
                              {item.listing_kind === "service" &&
                                item.slots_total && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[var(--t-950)] text-[var(--t-300)] border border-[var(--t-800-30)]">
                                    {item.slots_completed || 0}/
                                    {item.slots_total} slots filled
                                  </span>
                                )}
                              {item.listing_kind !== "service" &&
                                item.stock_quantity != null && (
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                                      item.stock_quantity <= 2
                                        ? "bg-red-500/10 text-red-400 border-red-500/30"
                                        : "bg-[var(--t-950)] text-[var(--t-300)] border-[var(--t-800-30)]"
                                    }`}
                                  >
                                    {item.stock_quantity} unit
                                    {item.stock_quantity === 1 ? "" : "s"} left
                                  </span>
                                )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-sm pl-[60px]">
                          <span className="text-[var(--t-200)] font-semibold">
                            KES {Number(item.price || 0).toLocaleString()}
                          </span>
                          <span className="text-[var(--t-200-60)] font-mono text-xs">
                            {(item.views || 0).toLocaleString()} views
                          </span>
                        </div>

                        <div className="flex items-center gap-2 pl-[60px]">
                          {item.listing_kind === "service" ? (
                            <button
                              type="button"
                              onClick={() => openBookingsModal(item)}
                              className="relative flex-1 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all bg-[var(--t-600)] hover:bg-[var(--t-500)] text-white shadow-md active:scale-95"
                            >
                              Bookings
                              {pendingBookingsByListing[item.id] > 0 && (
                                <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border border-[var(--t-950)]">
                                  {pendingBookingsByListing[item.id]}
                                </span>
                              )}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() =>
                                handleMarkAsSold(
                                  item.id,
                                  Number(item.price),
                                  item.stock_quantity
                                )
                              }
                              disabled={item.status === "sold"}
                              className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                                item.status === "sold"
                                  ? "bg-black text-[var(--t-500)] border border-[var(--t-800-30)] cursor-not-allowed"
                                  : "bg-[var(--t-600)] hover:bg-[var(--t-500)] text-white shadow-md active:scale-95"
                              }`}
                            >
                              {item.status === "sold"
                                ? "Sold"
                                : item.stock_quantity != null &&
                                  item.stock_quantity > 1
                                ? "Sell 1 Unit"
                                : "Mark as Sold"}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              requestDeleteListing(item.id, item.title)
                            }
                            className="flex-1 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all bg-red-900/40 hover:bg-red-800/60 text-red-300 border border-red-800/40 active:scale-95"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* DESKTOP / TABLET: original table, unchanged. */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-[var(--t-900-40)] text-[var(--t-200-50)] text-xs uppercase tracking-wider">
                          <th className="pb-3 font-medium">Item Details</th>
                          <th className="pb-3 font-medium">Price</th>
                          <th className="pb-3 font-medium">Traffic</th>
                          <th className="pb-3 font-medium text-right">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--t-900-20)] text-sm">
                        {listings.map((item: any) => (
                          <tr
                            key={item.id}
                            className="group hover:bg-[var(--t-950-10)] transition-colors duration-150"
                          >
                            <td className="py-4 pr-3">
                              <div className="flex items-center gap-3">
                                {item.photo_urls?.[0] ? (
                                  <img
                                    src={item.photo_urls[0]}
                                    alt={item.title}
                                    className="w-10 h-10 rounded-lg object-cover border border-[var(--t-900-30)]"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-lg bg-[var(--t-950-50)] border border-[var(--t-900-30)] flex items-center justify-center text-[var(--t-400)]">
                                    📦
                                  </div>
                                )}
                                <div>
                                  <span className="font-medium text-white block group-hover:text-[var(--t-300)] transition-colors">
                                    {item.title}
                                  </span>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[var(--t-950)] text-[var(--t-400)] border border-[var(--t-800-30)] mt-0.5">
                                    {item.status}
                                  </span>
                                  {item.listing_kind === "service" &&
                                    item.slots_total && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[var(--t-950)] text-[var(--t-300)] border border-[var(--t-800-30)] mt-0.5 ml-1.5">
                                        {item.slots_completed || 0}/
                                        {item.slots_total} slots filled
                                      </span>
                                    )}
                                  {item.listing_kind !== "service" &&
                                    item.stock_quantity != null && (
                                      <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border mt-0.5 ml-1.5 ${
                                          item.stock_quantity <= 2
                                            ? "bg-red-500/10 text-red-400 border-red-500/30"
                                            : "bg-[var(--t-950)] text-[var(--t-300)] border-[var(--t-800-30)]"
                                        }`}
                                      >
                                        {item.stock_quantity} unit
                                        {item.stock_quantity === 1
                                          ? ""
                                          : "s"}{" "}
                                        left
                                      </span>
                                    )}
                                </div>
                              </div>
                            </td>
                            <td className="py-4 text-[var(--t-200)]">
                              KES {Number(item.price || 0).toLocaleString()}
                            </td>
                            <td className="py-4 text-[var(--t-200-60)] font-mono text-xs">
                              {(item.views || 0).toLocaleString()} views
                            </td>
                            <td className="py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {item.listing_kind === "service" ? (
                                  <button
                                    type="button"
                                    onClick={() => openBookingsModal(item)}
                                    className="relative px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all bg-[var(--t-600)] hover:bg-[var(--t-500)] text-white shadow-md active:scale-95"
                                  >
                                    Bookings
                                    {pendingBookingsByListing[item.id] > 0 && (
                                      <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border border-[var(--t-950)]">
                                        {pendingBookingsByListing[item.id]}
                                      </span>
                                    )}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleMarkAsSold(
                                        item.id,
                                        Number(item.price),
                                        item.stock_quantity
                                      )
                                    }
                                    disabled={item.status === "sold"}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                                      item.status === "sold"
                                        ? "bg-black text-[var(--t-500)] border border-[var(--t-800-30)] cursor-not-allowed"
                                        : "bg-[var(--t-600)] hover:bg-[var(--t-500)] text-white shadow-md active:scale-95"
                                    }`}
                                  >
                                    {item.status === "sold"
                                      ? "Sold"
                                      : item.stock_quantity != null &&
                                        item.stock_quantity > 1
                                      ? "Sell 1 Unit"
                                      : "Mark as Sold"}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() =>
                                    requestDeleteListing(item.id, item.title)
                                  }
                                  className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all bg-red-900/40 hover:bg-red-800/60 text-red-300 border border-red-800/40 active:scale-95"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* STORE TAB — the seller's whole catalogue in full detail, plus
            their shareable store link. Buyers who open the link see a
            lighter, read-only version of this (see StorePage.tsx) and
            nothing else from the dashboard. */}
        {activeTab === "store" && (
          <div>
            <div className="mb-6">
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-white via-[var(--t-200)] to-[var(--t-400)] bg-clip-text text-transparent">
                Your Store
              </h1>
              <p className="text-[var(--t-200-60)] text-sm mt-0.5">
                Every listing in full detail. Share your link so buyers can
                browse your whole catalogue.
              </p>
            </div>

            {(() => {
              const storeUrl = storeCode ? buildStoreUrl(storeCode) : "";
              const canNativeShare =
                typeof navigator !== "undefined" &&
                typeof (navigator as any).share === "function";
              const handleCopyStoreLink = async () => {
                if (!storeUrl) return;
                const ok = await copyToClipboard(storeUrl);
                if (ok) {
                  setStoreLinkCopied(true);
                  setTimeout(() => setStoreLinkCopied(false), 2000);
                  showAppToast(
                    {
                      title: "Link copied",
                      message: "Your store link is ready to share.",
                      variant: "success",
                    },
                    3000
                  );
                } else {
                  showAppToast({
                    title: "Couldn't copy",
                    message: "Press and hold the link to copy it manually.",
                  });
                }
              };
              return (
                <div
                  ref={storeLinkCardRef}
                  className="rounded-2xl border border-[var(--t-900-40)] bg-[var(--t-950-20)] p-4 mb-6"
                >
                  <p className="text-xs uppercase tracking-wider text-[var(--t-200-50)] font-medium mb-2">
                    Your store link
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      onClick={handleCopyStoreLink}
                      disabled={!storeUrl}
                      title="Tap to copy"
                      className="flex-1 min-w-0 text-left px-3 py-2.5 rounded-xl bg-black/40 border border-[var(--t-900-40)] hover:border-[var(--t-500)] text-sm text-[var(--t-200)] truncate font-mono transition-colors"
                    >
                      {storeUrl ||
                        (storeInfoLoaded
                          ? "Your store link isn't ready yet — please refresh"
                          : "Preparing your link...")}
                    </button>
                    <div className="flex gap-2">
                      <button
                        ref={storeCopyBtnRef}
                        type="button"
                        onClick={handleCopyStoreLink}
                        disabled={!storeUrl}
                        className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--t-600)] hover:bg-[var(--t-500)] text-white text-sm font-bold active:scale-95 transition-all disabled:opacity-50"
                      >
                        {storeLinkCopied ? (
                          <Check size={16} />
                        ) : (
                          <Copy size={16} />
                        )}
                        {storeLinkCopied ? "Copied" : "Copy"}
                      </button>
                      {canNativeShare && storeUrl && (
                        <button
                          ref={storeShareBtnRef}
                          type="button"
                          onClick={() =>
                            (navigator as any)
                              .share({
                                title: `${sellerName} on INUA`,
                                text: `Browse ${sellerName}'s store on INUA`,
                                url: storeUrl,
                              })
                              .catch(() => {})
                          }
                          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[var(--t-200-20)] bg-[var(--t-950-20)] hover:bg-[var(--t-950-40)] text-[var(--t-200)] text-sm font-bold active:scale-95 transition-all"
                        >
                          <Share2 size={16} />
                          Share
                        </button>
                      )}
                      <button
                        ref={storeQrBtnRef}
                        type="button"
                        onClick={() => setIsQrSheetOpen(true)}
                        disabled={!storeUrl}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[var(--t-200-20)] bg-[var(--t-950-20)] hover:bg-[var(--t-950-40)] text-[var(--t-200)] text-sm font-bold active:scale-95 transition-all disabled:opacity-50"
                      >
                        <QrCode size={16} />
                        QR Code
                      </button>
                    </div>
                  </div>
                  <p className="text-[11px] text-[var(--t-200-50)] mt-2 leading-relaxed">
                    Buyers need to sign in to INUA to see your listings. They
                    only see your display name, location and listings.
                  </p>
                </div>
              );
            })()}

            {/* STORE HEADER — what buyers see at the top of your store */}
            <div ref={storeHeaderRef} className="flex items-center gap-4 mb-6">
              <div
                className={`w-16 h-16 rounded-full ${selectedAvatarObj.color} flex items-center justify-center text-2xl font-bold text-white shadow-lg shrink-0 border border-white/20`}
              >
                {sellerName.charAt(0).toUpperCase() || "S"}
              </div>
              <div className="min-w-0">
                <p className="text-xl font-extrabold text-white break-words">
                  {sellerName || "Your store"}
                </p>
                {storeLocationLine && (
                  <p className="flex items-center gap-1 text-xs text-[var(--t-200-60)] mt-1">
                    <MapPin size={12} className="shrink-0" />
                    <span className="break-words">{storeLocationLine}</span>
                  </p>
                )}
              </div>
            </div>

            {isLoadingDashboard ? (
              <div className="py-8 text-center">
                <Loader message="Loading your store" scale={0.4} />
              </div>
            ) : listings.length === 0 ? (
              <div className="py-12 text-center text-stone-400">
                Your store is empty. Create your first listing to fill it!
              </div>
            ) : (
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {listings.map((item: any, idx: number) => {
                  const photos: string[] = Array.isArray(item.photo_urls)
                    ? item.photo_urls
                    : [];
                  const isService = item.listing_kind === "service";
                  const subLabel = getSubcategoryDisplayName(item.subcategory);
                  const avail = item.availability || null;
                  const listedOn = item.created_at
                    ? new Date(item.created_at).toLocaleDateString("en-KE", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })
                    : "";
                  const detailRows: { label: string; value: string }[] = [];
                  if (isService) {
                    if (item.service_area)
                      detailRows.push({
                        label: "Service area",
                        value: String(item.service_area),
                      });
                    if (item.turnaround)
                      detailRows.push({
                        label: "Turnaround",
                        value: String(item.turnaround),
                      });
                    if (avail?.days?.length)
                      detailRows.push({
                        label: "Available",
                        value: `${avail.days.join(", ")}${
                          avail.start_time && avail.end_time
                            ? ` · ${avail.start_time}–${avail.end_time}`
                            : ""
                        }`,
                      });
                    if (item.slots_total)
                      detailRows.push({
                        label: "Slots",
                        value: `${item.slots_completed || 0} of ${
                          item.slots_total
                        } filled`,
                      });
                  } else {
                    if (item.stock_quantity != null)
                      detailRows.push({
                        label: "In stock",
                        value: `${item.stock_quantity} unit${
                          item.stock_quantity === 1 ? "" : "s"
                        }`,
                      });
                    if (item.execution_type)
                      detailRows.push({
                        label: "Type",
                        value:
                          item.execution_type === "PRE_ORDER"
                            ? `Pre-order${
                                item.target_capacity
                                  ? ` (${item.target_capacity} units)`
                                  : ""
                              }`
                            : "Live now",
                      });
                  }
                  return (
                    <div
                      key={item.id}
                      className="rounded-2xl border border-[var(--t-900-40)] bg-[var(--t-950-20)] overflow-hidden flex flex-col"
                    >
                      {photos.length > 0 ? (
                        <div
                          ref={idx === 0 ? storeFirstPhotosRef : undefined}
                          className="flex overflow-x-auto snap-x snap-mandatory gap-0.5 bg-black"
                        >
                          {photos.map((url, i) => (
                            <img
                              key={i}
                              src={url}
                              alt={`${item.title} ${i + 1}`}
                              className="snap-center shrink-0 w-full h-52 object-cover"
                            />
                          ))}
                        </div>
                      ) : (
                        <div
                          ref={idx === 0 ? storeFirstPhotosRef : undefined}
                          className="h-52 flex items-center justify-center bg-[var(--t-950-50)] text-4xl"
                        >
                          📦
                        </div>
                      )}
                      {photos.length > 1 && (
                        <p className="text-[10px] text-[var(--t-200-50)] text-center py-1 bg-black/30">
                          Swipe for {photos.length} photos
                        </p>
                      )}

                      <div
                        ref={idx === 0 ? storeFirstDetailsRef : undefined}
                        className="p-4 flex flex-col gap-3 flex-1"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            {subLabel && (
                              <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--t-400)] mb-0.5">
                                {subLabel}
                              </p>
                            )}
                            <h3 className="font-bold text-white text-lg leading-tight break-words">
                              {item.title}
                            </h3>
                          </div>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[var(--t-950)] text-[var(--t-400)] border border-[var(--t-800-30)] shrink-0">
                            {item.status}
                          </span>
                        </div>

                        <p className="text-[var(--t-200)] font-extrabold text-lg">
                          {isService ? "From " : ""}KES{" "}
                          {Number(item.price || 0).toLocaleString()}
                        </p>

                        {item.description && (
                          <p className="text-sm text-stone-300 leading-relaxed whitespace-pre-line break-words">
                            {item.description}
                          </p>
                        )}

                        {detailRows.length > 0 && (
                          <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-xs border-t border-[var(--t-900-30)] pt-3">
                            {detailRows.map((row) => (
                              <React.Fragment key={row.label}>
                                <dt className="text-[var(--t-200-50)] uppercase tracking-wider">
                                  {row.label}
                                </dt>
                                <dd className="text-[var(--t-200)] break-words">
                                  {row.value}
                                </dd>
                              </React.Fragment>
                            ))}
                          </dl>
                        )}

                        <div className="flex items-center justify-between text-xs text-[var(--t-200-60)] font-mono mt-auto pt-2">
                          <span>{(item.views || 0).toLocaleString()} views</span>
                          {listedOn && <span>Listed {listedOn}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ACCOUNT TAB — contact card editing, now a tab page instead of
            a modal overlay. Same underlying fields/validation/save logic
            as before; only the wrapper changed. */}
        {activeTab === "account" && (
          <div>
            <div className="mb-8">
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-white via-[var(--t-200)] to-[var(--t-400)] bg-clip-text text-transparent">
                Your Account
              </h1>
              <p className="text-[var(--t-200-60)] text-sm mt-0.5">
                Your public business card and account details.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              <div
                ref={accountEditCardRef}
                className="w-full rounded-2xl border border-[var(--t-900-40)] bg-gradient-to-b from-black to-[var(--t-950)] p-6 shadow-xl"
              >
                <h2 className="text-xl font-bold text-white mb-1">
                  Edit Contact Card
                </h2>

                <div className="p-3 bg-[var(--t-950-40)] border border-[var(--t-800-40)] rounded-xl mb-5 flex items-start gap-2.5">
                  <p className="text-xs text-[var(--t-200-80)] leading-relaxed">
                    These details form your public business card. Buyers will
                    see this information when contacting you from your listings.
                  </p>
                </div>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setDisplayNameError(null);

                    const trimmedName = sellerName.trim();
                    if (!trimmedName) {
                      setDisplayNameError("Display name cannot be empty.");
                      return;
                    }

                    if (!isValidKenyanPhone(phone)) {
                      showAppToast({
                        title: "Heads up",
                        message:
                          "Please enter your full phone number: +254 followed by 9 digits, e.g. +254712345678.",
                      });
                      return;
                    }

                    if (!isValidKenyanPhone(whatsapp)) {
                      showAppToast({
                        title: "Heads up",
                        message:
                          "Please enter your full WhatsApp number: +254 followed by 9 digits, e.g. +254712345678.",
                      });
                      return;
                    }

                    try {
                      const {
                        data: { user },
                      } = await supabase.auth.getUser();
                      if (!user) return;

                      // Only sellers setting their display name for the FIRST time
                      // need the uniqueness check + get it locked afterward. If it's
                      // already locked, we never touch display_name in the update at all.
                      const isSettingNameForFirstTime = !isDisplayNameLocked;

                      if (isSettingNameForFirstTime) {
                        setIsCheckingDisplayName(true);
                        // Case-insensitive "is this name already taken by someone else" check,
                        // mirroring how usernames work elsewhere (e.g. Instagram).
                        const { data: existing, error: checkError } =
                          await supabase
                            .from("seller_applications")
                            .select("id")
                            .ilike("display_name", trimmedName)
                            .neq("id", user.id)
                            .maybeSingle();
                        setIsCheckingDisplayName(false);

                        if (checkError) throw checkError;
                        if (existing) {
                          setDisplayNameError(
                            "That display name is already taken. Please choose another one — this can't be changed later."
                          );
                          return;
                        }
                      }

                      writeOwnedCache(
                        "seller_display_name",
                        user.id,
                        trimmedName
                      );

                      const updatePayload: Record<string, any> = {
                        phone_number: phone,
                        whatsapp_number: whatsapp,
                        avatar_id: avatarId,
                      };
                      // Never send display_name in the update once it's locked —
                      // this is a belt-and-suspenders guard alongside the disabled input.
                      if (isSettingNameForFirstTime) {
                        updatePayload.display_name = trimmedName;
                      }

                      const { error } = await supabase
                        .from("seller_applications")
                        .update(updatePayload)
                        .eq("id", user.id);

                      if (error) {
                        // Postgres unique-constraint violation code — a safety net in
                        // case two sellers submit the same name in the same instant.
                        if (error.code === "23505") {
                          setDisplayNameError(
                            "That display name was just taken by someone else. Please choose another one."
                          );
                          return;
                        }
                        throw error;
                      }

                      setOriginalDbData({
                        display_name: trimmedName,
                        phone_number: phone,
                        whatsapp_number: whatsapp,
                        avatar_id: avatarId,
                      });
                      if (isSettingNameForFirstTime) {
                        setIsDisplayNameLocked(true);
                      }

                      showAppToast({
                        title: "Saved",
                        message: "Your contact card has been updated.",
                        variant: "success",
                      });
                      setActiveTab("home");
                    } catch (err: any) {
                      console.error("Error saving contact card:", err);
                      showAppToast({
                        title: "Something went wrong",
                        message: `Failed to sync contact card: ${
                          err.message || err
                        }`,
                      });
                    }
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="text-xs font-semibold text-[var(--t-200-80)] block mb-2">
                      Choose Avatar Badge
                    </label>
                    <div className="grid grid-cols-5 gap-2">
                      {AVATAR_PRESETS.map((preset: any) => {
                        const isSelected = avatarId === preset.id;
                        return (
                          <button
                            key={preset.id}
                            type="button"
                            onClick={() => setAvatarId(preset.id)}
                            className={`w-10 h-10 rounded-full ${
                              preset.color
                            } flex items-center justify-center text-sm font-bold text-white transition-all border ${
                              isSelected
                                ? "ring-2 ring-white scale-110 border-white"
                                : "border-transparent opacity-70 hover:opacity-100"
                            }`}
                          >
                            {sellerName.charAt(0).toUpperCase() || "S"}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[var(--t-200-80)]">
                      Display Name
                    </label>
                    <SmoothInput
                      type="text"
                      value={sellerName}
                      onChange={(e) => {
                        setSellerName(e.target.value);
                        setDisplayNameError(null);
                      }}
                      disabled={isDisplayNameLocked}
                      className={`w-full bg-[var(--t-950-20)] border border-[var(--t-900-40)] rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-[var(--t-500-60)] transition-colors ${
                        isDisplayNameLocked
                          ? "opacity-60 cursor-not-allowed"
                          : ""
                      }`}
                      placeholder="e.g., Morata Crafts"
                      required
                    />
                    {isDisplayNameLocked ? (
                      <p className="text-[11px] text-[var(--t-300-70)] leading-snug">
                        Your display name is permanently set and can't be
                        changed. This keeps you accountable to buyers under one
                        consistent name.
                      </p>
                    ) : sellerName.length > 0 ? (
                      <p className="text-[11px] text-[var(--t-400)] leading-snug">
                        NOTE: you can only set your display name once. Once
                        saved, it can never be changed — choose carefully.
                      </p>
                    ) : null}
                    {displayNameError && (
                      <p className="text-[11px] text-red-400 leading-snug">
                        {displayNameError}
                      </p>
                    )}
                    {isCheckingDisplayName && (
                      <p className="text-[11px] text-[var(--t-300-60)] leading-snug">
                        Checking availability…
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[var(--t-200-80)]">
                      Phone Number (Calls)
                    </label>
                    <SmoothInput
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(formatKenyanPhone(e.target.value))}
                      className="w-full bg-[var(--t-950-20)] border border-[var(--t-900-40)] rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-[var(--t-500-60)] transition-colors"
                      placeholder="+254712345678"
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[var(--t-200-80)]">
                      WhatsApp Number
                    </label>
                    <SmoothInput
                      type="tel"
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(formatKenyanPhone(e.target.value))}
                      className="w-full bg-[var(--t-950-20)] border border-[var(--t-900-40)] rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-[var(--t-500-60)] transition-colors"
                      placeholder="+254712345678"
                      required
                    />
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--t-900-20)] mt-6">
                    <button
                      type="button"
                      onClick={() => setActiveTab("home")}
                      className="px-4 py-2 rounded-xl text-sm font-medium text-[var(--t-200-60)] hover:text-[var(--t-200)] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isFormUnchanged || isCheckingDisplayName}
                      className="px-5 py-2 rounded-xl text-sm font-semibold bg-white text-[#1c0d02] hover:bg-[var(--t-50)] shadow-md transition-all active:scale-[0.98] disabled:opacity-50"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>

              {/* ACCOUNT OVERVIEW — read-only recap of what's currently
                saved, sitting alongside the edit form so a seller can see
                their live business card without having to save/reload. */}
              <div
                ref={accountOverviewRef}
                className="w-full rounded-2xl border border-[var(--t-900-40)] bg-gradient-to-b from-black to-[var(--t-950)] p-6 shadow-xl"
              >
                <h2 className="text-xl font-bold text-white mb-5">
                  Account Overview
                </h2>

                <div className="flex items-center gap-3 mb-6">
                  <div
                    className={`w-14 h-14 rounded-full ${selectedAvatarObj.color} flex items-center justify-center text-xl font-bold text-white shadow-lg shrink-0 border border-white/20`}
                  >
                    {sellerName.charAt(0).toUpperCase() || "S"}
                  </div>
                  <div>
                    <p className="text-white font-semibold">{sellerName}</p>
                    <p className="text-xs text-[var(--t-200-50)]">
                      {sellerListingType === "service"
                        ? "Service seller"
                        : "Product seller"}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--t-200-50)] mb-1">
                      Phone Number
                    </p>
                    <p className="text-sm text-white">
                      {hasPhoneDigits(phone) ? (
                        phone
                      ) : (
                        <span className="text-[var(--t-200-40)]">Not set</span>
                      )}
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--t-200-50)] mb-1">
                      WhatsApp Number
                    </p>
                    <p className="text-sm text-white">
                      {hasPhoneDigits(whatsapp) ? (
                        whatsapp
                      ) : (
                        <span className="text-[var(--t-200-40)]">Not set</span>
                      )}
                    </p>
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--t-200-50)] mb-1">
                      Category
                    </p>
                    <span className="inline-block px-2.5 py-1 rounded-lg text-xs font-medium bg-[var(--t-950-40)] border border-[var(--t-800-40)] text-[var(--t-200)]">
                      {sellerParentCategory?.name || "Uncategorized"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 rounded-2xl border border-[var(--t-900-40)] bg-gradient-to-b from-black to-[var(--t-950)] p-6 shadow-xl">
              <h2 className="text-xl font-bold text-white mb-1">Sign out</h2>
              <p className="text-xs text-[var(--t-200-60)] leading-relaxed mb-4">
                Used someone else's phone or computer? Log out when you're done
                so nobody else can open your dashboard. If you forgot to log
                out somewhere, use "Log out of all devices".
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => requestSignOut("local")}
                  className="flex items-center justify-center gap-2 border border-[var(--t-200-20)] bg-[var(--t-950-20)] hover:bg-[var(--t-950-40)] text-[var(--t-200)] px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
                >
                  <LogOut size={18} />
                  Log out
                </button>
                <button
                  type="button"
                  onClick={() => requestSignOut("global")}
                  className="flex items-center justify-center gap-2 border border-[var(--t-200-20)] bg-transparent hover:bg-[var(--t-950-40)] text-[var(--t-200)] px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
                >
                  <LogOut size={18} />
                  Log out of all devices
                </button>
              </div>
            </div>
          </div>
        )}

        {/* REPORT TAB — this seller's month in numbers, compared with the best
            and the typical seller in the same category once enough sellers
            in that category have activity. Numbers come from the
            get_seller_monthly_report database function. */}
        {activeTab === "report" && (
          <div>
            <div className="mb-6">
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-white via-[var(--t-200)] to-[var(--t-400)] bg-clip-text text-transparent">
                Your Report
              </h1>
              <p className="text-[var(--t-200-60)] text-sm mt-0.5">
                How your store performed, and how you compare in{" "}
                {sellerParentCategory?.name || "your category"}.
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-2">
              {(["last", "this"] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setReportPeriod(p)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${
                    reportPeriod === p
                      ? "bg-white text-[#1c0d02] border-white"
                      : "border-[var(--t-200-20)] bg-[var(--t-950-20)] text-[var(--t-200)] hover:bg-[var(--t-950-40)]"
                  }`}
                >
                  {p === "last" ? "Last month" : "This month so far"}
                </button>
              ))}
            </div>
            {report?.month_start && (
              <p className="text-xs text-[var(--t-200-60)] mb-5">
                {formatReportRange(report.month_start, report.month_end)}
              </p>
            )}

            {reportStatus === "loading" && !report && (
              <p className="text-sm text-[var(--t-200-60)] py-8">
                Loading your report…
              </p>
            )}

            {reportStatus === "error" && (
              <div className="rounded-2xl border border-[var(--t-900-40)] bg-black/30 p-5 mt-4">
                <p className="text-sm text-[var(--t-200)] mb-3">
                  We could not load your report. Check your connection and try
                  again.
                </p>
                <button
                  type="button"
                  onClick={() => setReportReloadKey((k) => k + 1)}
                  className="border border-[var(--t-200-20)] bg-[var(--t-950-20)] hover:bg-[var(--t-950-40)] text-[var(--t-200)] px-4 py-2 rounded-xl text-sm font-medium transition-colors"
                >
                  Try again
                </button>
              </div>
            )}

            {report && reportStatus !== "error" && (
              <div className="flex flex-col gap-4">
                {REPORT_METRICS.every((m) => !Number(report.me?.[m.key] || 0)) && (
                  <p className="text-xs text-[var(--t-200-60)] leading-relaxed">
                    No activity recorded for this period yet. Views and contact
                    taps are counted from when tracking started, so your first
                    month may look low.
                  </p>
                )}

                {REPORT_METRICS.map((m) => {
                  const mine = Number(report.me?.[m.key] || 0);
                  const best = Number(report.best?.[m.key] || 0);
                  const typical = Number(report.median?.[m.key] || 0);
                  const ahead = report.ahead_of?.[m.key];
                  const barPct =
                    best > 0 ? Math.min(100, Math.round((mine / best) * 100)) : 0;
                  return (
                    <div
                      key={m.key}
                      className="rounded-2xl border border-[var(--t-900-40)] bg-gradient-to-b from-black to-[var(--t-950)] p-5 shadow-xl"
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-sm font-semibold text-white">
                          {m.label}
                        </p>
                        <p className="text-2xl font-bold text-white">
                          {formatReportValue(mine, m.money)}
                        </p>
                      </div>
                      <p className="text-[11px] text-[var(--t-200-60)] mt-0.5">
                        {m.hint}
                      </p>

                      {report.comparable && report.best && (
                        <div className="mt-4">
                          <div className="h-2 rounded-full bg-[var(--t-950-40)] overflow-hidden">
                            <div
                              className="h-full rounded-full bg-[var(--t-400)]"
                              style={{ width: `${barPct}%` }}
                            />
                          </div>
                          <div className="flex justify-between text-[11px] text-[var(--t-200-60)] mt-1.5">
                            <span>
                              Typical seller: {formatReportValue(typical, m.money)}
                            </span>
                            <span>
                              Best: {formatReportValue(best, m.money)}
                            </span>
                          </div>
                          {typeof ahead === "number" && (
                            <p className="text-xs text-[var(--t-200)] mt-2">
                              You are ahead of {Math.round(ahead)}% of other
                              active sellers in{" "}
                              {sellerParentCategory?.name || "your category"}.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {!report.comparable && (
                  <p className="text-xs text-[var(--t-200-60)] leading-relaxed">
                    Comparison with other sellers appears once 5 sellers in{" "}
                    {sellerParentCategory?.name || "your category"} have
                    activity in the same period. So far: {report.peer_count || 1}.
                  </p>
                )}

                <p className="text-[11px] text-[var(--t-200-50)] leading-relaxed">
                  Sales and earnings count orders and bookings you mark
                  completed in INUA; deals agreed outside the app are not
                  included. Other sellers are never named.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ORDERS TAB — full sales/booking-completion history, pulling
            from the same `orders` table the metrics grid's "Total
            Earnings" figure is summed from. */}
        {activeTab === "orders" && (
          <div>
            <div className="mb-8">
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-white via-[var(--t-200)] to-[var(--t-400)] bg-clip-text text-transparent">
                Your Orders
              </h1>
              <p className="text-[var(--t-200-60)] text-sm mt-0.5">
                Track booking requests and past sales in one place.
              </p>
            </div>

            {/* CURRENT / PENDING ORDERS */}
            <div
              ref={ordersPendingRef}
              className="bg-[var(--t-950-10)] border border-[var(--t-900-30)] backdrop-blur-md rounded-2xl p-6 shadow-xl mb-6"
            >
              <h2 className="text-lg font-semibold mb-4 tracking-wide text-[var(--t-100)]">
                Current &amp; Pending Orders
              </h2>

              {isLoadingPendingOrders ? (
                <div className="py-8 text-center">
                  <Loader message="Loading your orders" scale={0.4} />
                </div>
              ) : pendingOrders.length === 0 ? (
                <div className="py-8 text-center text-stone-400">
                  No pending booking requests right now.
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingOrders.map((booking: any) => {
                    const listingForBooking = listings.find(
                      (l: any) => l.id === booking.listing_id
                    );
                    const isPending = booking.status === "pending";

                    return (
                      <div
                        key={booking.id}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl border border-[var(--t-900-30)] bg-[var(--t-950-20)]"
                      >
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-white text-sm font-semibold">
                              {booking.buyer_name || "A buyer"}
                            </p>
                            <span
                              className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                isPending
                                  ? "bg-amber-500/15 text-amber-300"
                                  : "bg-emerald-500/15 text-emerald-300"
                              }`}
                            >
                              {isPending ? "Pending" : "Confirmed"}
                            </span>
                          </div>
                          <p className="text-xs text-[var(--t-200-60)] mt-1">
                            {listingForBooking?.title || "Listing removed"} ·{" "}
                            {booking.booking_date} at {booking.booking_time}
                          </p>
                          {booking.buyer_phone && (
                            <p className="text-xs text-[var(--t-200-40)] mt-0.5">
                              {booking.buyer_phone}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {isPending ? (
                            <>
                              <button
                                onClick={() =>
                                  requestConfirmBooking(
                                    booking,
                                    listingForBooking
                                  )
                                }
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() =>
                                  handleBookingStatusChange(
                                    booking,
                                    "declined",
                                    listingForBooking
                                  )
                                }
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/15 text-red-300 hover:bg-red-500/25 transition-colors"
                              >
                                Decline
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() =>
                                handleBookingStatusChange(
                                  booking,
                                  "completed",
                                  listingForBooking
                                )
                              }
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white text-[#1c0d02] hover:bg-[var(--t-50)] transition-colors"
                            >
                              Mark Completed
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* COMPLETED ORDERS — most recent sales/booking-completions
                only (last 10, see the .limit(10) in fetchSellerOrders),
                pulling from the same `orders` table the metrics grid's
                "Total Earnings" figure is summed from. */}
            <div
              ref={ordersRecentRef}
              className="bg-[var(--t-950-10)] border border-[var(--t-900-30)] backdrop-blur-md rounded-2xl p-6 shadow-xl"
            >
              <h2 className="text-lg font-semibold mb-4 tracking-wide text-[var(--t-100)]">
                Recent Orders
              </h2>
              <p className="text-xs text-[var(--t-200-50)] -mt-3 mb-4">
                Your last 10 completed orders.
              </p>

              {isLoadingOrders ? (
                <div className="py-8 text-center">
                  <Loader message="Loading your orders" scale={0.4} />
                </div>
              ) : sellerOrders.length === 0 ? (
                <div className="py-8 text-center text-stone-400">
                  No orders yet. Once you mark an item sold or complete a
                  booking, it'll show up here.
                </div>
              ) : (
                <div className="overflow-x-auto -mx-6">
                  <table className="w-full min-w-[560px]">
                    <thead>
                      <tr className="text-left border-b border-[var(--t-900-30)]">
                        <th className="px-6 pb-3 text-xs font-semibold uppercase tracking-wider text-[var(--t-200-50)]">
                          Item
                        </th>
                        <th className="px-6 pb-3 text-xs font-semibold uppercase tracking-wider text-[var(--t-200-50)]">
                          Amount
                        </th>
                        <th className="px-6 pb-3 text-xs font-semibold uppercase tracking-wider text-[var(--t-200-50)]">
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sellerOrders.map((order: any) => {
                        // Matched against the dashboard's own already-loaded
                        // `listings` state (same array the Active Listings
                        // table renders from) rather than a second Supabase
                        // query — see the comment above fetchSellerOrders.
                        const listingForOrder = listings.find(
                          (l: any) => l.id === order.listing_id
                        );
                        const listingImage =
                          listingForOrder?.image_url ||
                          listingForOrder?.images?.[0] ||
                          null;
                        const orderDate = order.created_at
                          ? new Date(order.created_at).toLocaleDateString(
                              "en-KE",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              }
                            )
                          : "—";

                        return (
                          <tr
                            key={order.id}
                            className="border-b border-[var(--t-900-20)] last:border-0"
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-[var(--t-950-40)] border border-[var(--t-900-30)] overflow-hidden shrink-0 flex items-center justify-center">
                                  {listingImage ? (
                                    <img
                                      src={listingImage}
                                      alt=""
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <ClipboardList
                                      size={16}
                                      className="text-[var(--t-300-60)]"
                                    />
                                  )}
                                </div>
                                <p className="text-white text-sm font-medium">
                                  {listingForOrder?.title || "Listing removed"}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-[var(--t-300)] font-semibold">
                              KES {Number(order.amount || 0).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-sm text-[var(--t-200-60)]">
                              {orderDate}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CREATE NEW LISTING MODAL OVERLAY */}
        {isCreateModalOpen && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-gradient-to-b from-black to-[var(--t-950)] border border-[var(--t-900-30)] rounded-2xl w-full max-w-lg p-6 my-8 shadow-2xl relative animate-fadeIn">
              {!isSuccessScreen && (
                <>
                  <h3 className="text-xl font-bold text-white mb-1 uppercase tracking-wider text-[var(--t-50)] bg-gradient-to-r from-[var(--t-400)] to-[var(--t-200)] bg-clip-text text-transparent">
                    {sellerListingType === "service"
                      ? "List a New Service"
                      : "Create New Listing"}
                  </h3>
                  <p className="text-stone-400 text-xs mb-6">
                    {sellerListingType === "service"
                      ? "Fill out the details to list your service on the marketplace."
                      : "Fill out the details to upload your product to the marketplace."}
                  </p>
                </>
              )}

              <div className="space-y-5">
                {isSuccessScreen ? (
                  <div className="text-center py-12 animate-fadeIn space-y-6">
                    <div className="w-16 h-16 bg-gradient-to-tr from-[var(--t-500)] to-[var(--t-400)] rounded-full flex items-center justify-center mx-auto shadow-lg shadow-[var(--t-500-20)] animate-bounce">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="3"
                        stroke="currentColor"
                        className="w-8 h-8 text-white"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-2xl font-bold text-white uppercase tracking-wide">
                        Listing successfully uploaded!
                      </h4>
                      <p className="text-stone-400 text-sm max-w-xs mx-auto">
                        Your item is now live and available for buyers.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setIsCreateModalOpen(false);
                        setIsSuccessScreen(false);
                        fetchDashboardData();
                      }}
                      className="mt-4 px-8 py-3 rounded-xl text-xs font-bold uppercase tracking-widest bg-white text-[#1c0d02] hover:bg-[var(--t-50)] active:scale-[0.98] transition-all shadow-md"
                    >
                      Go to Dashboard
                    </button>
                  </div>
                ) : (
                  <Stepper
                    initialStep={createListingStep}
                    onStepChange={setCreateListingStep}
                    backButtonText="Previous"
                    nextButtonText="Next"
                    completeButtonText={isUploading ? "Publishing..." : "INUA"}
                    accentColor={stepperAccentColor}
                    accentColorHover={stepperAccentColorHover}
                    nextButtonProps={
                      createListingStep === CREATE_LISTING_TOTAL_STEPS
                        ? {
                            type: "button",
                            disabled: isUploading,
                            onClick: handleCreateListingSubmit,
                          }
                        : createListingStep === 3
                        ? {
                            // Photos step: block moving on while any picked
                            // image is still decoding, same idea as Claude's
                            // own attach UX — the thumbnail is visible but
                            // "not clear yet" until it's done loading.
                            disabled:
                              imagePreviews.length > 0 && !allPreviewsLoaded,
                          }
                        : {}
                    }
                    stepCircleContainerClassName="!shadow-none !max-w-none"
                    contentClassName="!px-0"
                  >
                    <Step>
                      <div className="space-y-5">
                        <p className="text-xs font-bold text-[var(--t-300)] uppercase tracking-widest mb-1">
                          Step 1 — The Basics
                        </p>
                        <div>
                          <label className="block text-xs font-semibold text-[var(--t-200-70)] uppercase tracking-wider mb-2">
                            Subcategory
                          </label>
                          <select
                            required
                            value={selectedSubcategory}
                            onChange={(e) =>
                              setSelectedSubcategory(e.target.value)
                            }
                            className="w-full bg-black text-white border border-[var(--t-900-40)] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--t-500)] transition-all"
                          >
                            <option value="" disabled>
                              Select the specific type of listing this is
                            </option>
                            {(sellerParentCategory?.subcategories || []).map(
                              (sub: any) => (
                                <option key={sub.id} value={sub.id}>
                                  {sub.name}
                                </option>
                              )
                            )}
                          </select>
                          <p className="text-[11px] text-stone-500 mt-1.5">
                            Buyers see this specific tag on your listing card —
                            it's more precise than your overall{" "}
                            {sellerParentCategory?.name || "category"}.
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-[var(--t-200-70)] uppercase tracking-wider mb-2">
                            {sellerListingType === "service"
                              ? "Service Name"
                              : "Product Name"}
                          </label>
                          <SmoothInput
                            type="text"
                            required
                            placeholder={
                              sellerListingType === "service"
                                ? "e.g., Home Hairdressing & Braiding"
                                : "e.g., Handcrafted Mahogany Coffee Table"
                            }
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                            className="w-full bg-black text-white border border-[var(--t-900-40)] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--t-500)] transition-all placeholder:text-stone-600"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-[var(--t-200-70)] uppercase tracking-wider mb-2">
                            {sellerListingType === "service"
                              ? "Starting Price (KES)"
                              : "Price (KES)"}
                          </label>
                          <SmoothInput
                            type="number"
                            required
                            placeholder="Price in KES"
                            value={productPrice}
                            onChange={(e) => setProductPrice(e.target.value)}
                            className="w-full bg-black text-white border border-[var(--t-900-40)] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--t-500)] transition-all placeholder:text-stone-600"
                          />
                        </div>
                      </div>
                    </Step>

                    <Step>
                      <div className="space-y-5">
                        <p className="text-xs font-bold text-[var(--t-300)] uppercase tracking-widest mb-1">
                          Step 2 — Details
                        </p>
                        {sellerListingType === "service" ? (
                          <React.Fragment>
                            <div>
                              <label className="block text-xs font-semibold text-[var(--t-200-70)] uppercase tracking-wider mb-2">
                                Service Area
                              </label>
                              <SmoothInput
                                type="text"
                                required
                                placeholder="e.g., On Campus (Main Gate area), or Remote"
                                value={serviceArea}
                                onChange={(e) => setServiceArea(e.target.value)}
                                className="w-full bg-black text-white border border-[var(--t-900-40)] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--t-500)] transition-all placeholder:text-stone-600"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-[var(--t-200-70)] uppercase tracking-wider mb-2">
                                Typical Turnaround{" "}
                                <span className="normal-case text-stone-500">
                                  (optional)
                                </span>
                              </label>
                              <SmoothInput
                                type="text"
                                placeholder="e.g., Same day, 24 hours, 2-3 days"
                                value={turnaround}
                                onChange={(e) => setTurnaround(e.target.value)}
                                className="w-full bg-black text-white border border-[var(--t-900-40)] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--t-500)] transition-all placeholder:text-stone-600"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-[var(--t-200-70)] uppercase tracking-wider mb-2">
                                Availability
                              </label>
                              <div className="grid grid-cols-7 gap-1.5 mb-3">
                                {WEEKDAYS.map((day: any) => (
                                  <button
                                    key={day}
                                    type="button"
                                    onClick={() => toggleAvailDay(day)}
                                    className={`py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${
                                      availDays.includes(day)
                                        ? "bg-[var(--t-600)] text-white shadow"
                                        : "bg-black border border-[var(--t-900-40)] text-stone-400 hover:text-stone-200"
                                    }`}
                                  >
                                    {day}
                                  </button>
                                ))}
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-[11px] text-stone-500 uppercase tracking-wider mb-1">
                                    From
                                  </label>
                                  <input
                                    type="time"
                                    required
                                    value={availStart}
                                    onChange={(e) =>
                                      setAvailStart(e.target.value)
                                    }
                                    className="w-full bg-black text-white border border-[var(--t-900-40)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--t-500)] transition-all"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[11px] text-stone-500 uppercase tracking-wider mb-1">
                                    To
                                  </label>
                                  <input
                                    type="time"
                                    required
                                    value={availEnd}
                                    onChange={(e) =>
                                      setAvailEnd(e.target.value)
                                    }
                                    className="w-full bg-black text-white border border-[var(--t-900-40)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--t-500)] transition-all"
                                  />
                                </div>
                              </div>
                              <div className="mt-3">
                                <label className="block text-[11px] text-stone-500 uppercase tracking-wider mb-1">
                                  Booking Slot Length
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                  {[30, 60, 90].map((mins: any) => (
                                    <button
                                      key={mins}
                                      type="button"
                                      onClick={() => setSlotMinutes(mins)}
                                      className={`py-2 rounded-lg text-xs font-bold transition-all ${
                                        slotMinutes === mins
                                          ? "bg-[var(--t-600)] text-white shadow"
                                          : "bg-black border border-[var(--t-900-40)] text-stone-400 hover:text-stone-200"
                                      }`}
                                    >
                                      {mins} min
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <p className="text-[11px] text-stone-500 mt-2">
                                Buyers will be able to book a slot within this
                                window on the days you've selected.
                              </p>

                              <div className="mt-4">
                                {isOpen24hSubcategory && (
                                  <p className="text-[11px] text-[var(--t-300)] bg-[var(--t-900-20)] border border-[var(--t-800-40)] rounded-lg px-3 py-2 leading-relaxed">
                                    Laundry listings are open, walk-in-style
                                    bookings with no slot cap. Since laundry
                                    needs quick pickup, this listing
                                    automatically comes off the buyer feed 24
                                    hours after you post it — post a fresh one
                                    anytime.
                                  </p>
                                )}

                                {isHomeServicesCategory &&
                                  selectedSubcategory === "baby_sitting" && (
                                    <p className="text-[11px] text-[var(--t-300)] bg-[var(--t-900-20)] border border-[var(--t-800-40)] rounded-lg px-3 py-2 leading-relaxed">
                                      Baby Sitting doesn't use a slots cap — a
                                      sitter can only watch one child at a time,
                                      and a booked time slot is automatically
                                      unavailable to anyone else. This listing
                                      stays live indefinitely.
                                    </p>
                                  )}

                                {!hidesCapacityField && (
                                  <>
                                    <label className="block text-[11px] text-stone-500 uppercase tracking-wider mb-1">
                                      Total Slots Available
                                      {isSlotRequiredSubcategory && (
                                        <span className="text-[var(--t-400)] normal-case">
                                          {" "}
                                          (required)
                                        </span>
                                      )}
                                    </label>
                                    <SmoothInput
                                      type="number"
                                      min={1}
                                      required={isSlotRequiredSubcategory}
                                      placeholder="e.g. 3"
                                      value={capacitySlots}
                                      onChange={(e) =>
                                        setCapacitySlots(e.target.value)
                                      }
                                      className="w-full bg-black text-white border border-[var(--t-900-40)] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--t-500)] transition-all"
                                    />
                                    <p className="text-[11px] text-stone-500 mt-2">
                                      {isSlotRequiredSubcategory
                                        ? "How many bookings this listing can fulfill. This isn't an always-open service, so buyers won't see it once every slot is marked completed — put up a new listing for your next batch."
                                        : "How many bookings this specific listing can fulfill. Once every slot is marked completed, it's automatically taken off the buyer feed — handy for one-off styles or batches rather than an always-open service. Leave blank for an ongoing listing with no cap."}
                                    </p>
                                  </>
                                )}
                              </div>
                            </div>
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            <div>
                              <label className="block text-xs font-semibold text-[var(--t-200-70)] uppercase tracking-wider mb-2">
                                Listing Execution Method
                              </label>
                              <div className="grid grid-cols-2 gap-3 p-1 bg-black border border-[var(--t-900-40)] rounded-xl">
                                <button
                                  type="button"
                                  onClick={() => setListingType("LIVE")}
                                  className={`py-2 px-3 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                                    listingType === "LIVE"
                                      ? "bg-[var(--t-600)] text-white shadow"
                                      : "text-stone-400 hover:text-stone-200"
                                  }`}
                                >
                                  Live Now
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setListingType("PRE_ORDER")}
                                  className={`py-2 px-3 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                                    listingType === "PRE_ORDER"
                                      ? "bg-[var(--t-600)] text-white shadow"
                                      : "text-stone-400 hover:text-stone-200"
                                  }`}
                                >
                                  Pre-Order
                                </button>
                              </div>
                            </div>

                            {listingType === "PRE_ORDER" && (
                              <div className="animate-fadeIn">
                                <label className="block text-xs font-semibold text-[var(--t-400)] uppercase tracking-wider mb-2">
                                  Target Capacity (Units)
                                </label>
                                <SmoothInput
                                  type="number"
                                  required
                                  min="1"
                                  placeholder="How many orders can you take before production?"
                                  value={preorderUnits}
                                  onChange={(e) =>
                                    setPreorderUnits(e.target.value)
                                  }
                                  className="w-full bg-black text-white border border-[var(--t-500-30)] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--t-500)] transition-all"
                                />
                              </div>
                            )}

                            {listingType === "LIVE" && (
                              <div className="animate-fadeIn">
                                <label className="block text-xs font-semibold text-[var(--t-400)] uppercase tracking-wider mb-2">
                                  Units To Sell
                                </label>
                                <SmoothInput
                                  type="number"
                                  required
                                  min="1"
                                  placeholder="How many do you have in stock right now?"
                                  value={unitsToSell}
                                  onChange={(e) =>
                                    setUnitsToSell(e.target.value)
                                  }
                                  className="w-full bg-black text-white border border-[var(--t-500-30)] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--t-500)] transition-all"
                                />
                                <p className="text-[11px] text-stone-500 mt-2">
                                  {Number(unitsToSell) > 0 &&
                                  Number(productPrice) > 0 ? (
                                    <>
                                      Potential shop value:{" "}
                                      <span className="text-[var(--t-300)] font-semibold">
                                        KES{" "}
                                        {(
                                          Number(unitsToSell) *
                                          Number(productPrice)
                                        ).toLocaleString()}
                                      </span>
                                    </>
                                  ) : (
                                    "Buyers only see a low-stock warning once you're down to your last 1-2 units — the exact count stays visible to you alone here in your dashboard."
                                  )}
                                </p>
                              </div>
                            )}
                          </React.Fragment>
                        )}
                      </div>
                    </Step>

                    <Step>
                      <div className="space-y-5">
                        <p className="text-xs font-bold text-[var(--t-300)] uppercase tracking-widest mb-1">
                          Step 3 — Description & Photos
                        </p>
                        <div>
                          <label className="block text-xs font-semibold text-[var(--t-200-70)] uppercase tracking-wider mb-2">
                            Short Description
                          </label>
                          <textarea
                            required
                            rows={3}
                            maxLength={DESCRIPTION_MAX_LENGTH}
                            placeholder={
                              sellerListingType === "service"
                                ? "Give buyers details about what's included, your experience, or how booking works..."
                                : "Give buyers details about materials, craft times, or delivery terms..."
                            }
                            value={productDescription}
                            onChange={(e) =>
                              setProductDescription(
                                stripDigits(e.target.value).slice(
                                  0,
                                  DESCRIPTION_MAX_LENGTH
                                )
                              )
                            }
                            className="w-full bg-black text-white border border-[var(--t-900-40)] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--t-500)] transition-all placeholder:text-stone-600 resize-none"
                          />
                          <div className="flex justify-between items-center mt-1.5 px-0.5">
                            <span className="text-[10px] text-stone-500">
                              No numbers or pricing language — set your price in
                              the listing price field.
                            </span>
                            <span className="text-[10px] text-stone-500 shrink-0 ml-2">
                              {productDescription.length}/
                              {DESCRIPTION_MAX_LENGTH}
                            </span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-[var(--t-200-70)] uppercase tracking-wider mb-2">
                            {sellerListingType === "service"
                              ? "Portfolio Photos (Upload 3-6 Photos)"
                              : "Product Images (Upload 3-6 Photos)"}
                          </label>
                          <div className="border-2 border-dashed border-[var(--t-900-30)] rounded-xl p-4 text-center hover:border-[var(--t-500-40)] transition-all bg-black/50 relative">
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={handleImageChange}
                              disabled={isUploading}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                            />
                            <p className="text-xs text-stone-400 font-medium">
                              Click to pick your gallery files
                            </p>
                          </div>

                          {imagePreviews.length > 0 && (
                            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                              {imagePreviews.map((src, index) => (
                                <div
                                  key={index}
                                  className="relative w-14 h-14 flex-shrink-0"
                                >
                                  <img
                                    src={src}
                                    alt="Preview"
                                    onLoad={() => markPreviewLoaded(index)}
                                    className={`w-14 h-14 object-cover rounded-lg border border-[var(--t-900-40)] transition-opacity duration-500 ${
                                      loadedPreviews[index]
                                        ? "opacity-100"
                                        : "opacity-40 animate-pulse"
                                    }`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveImage(index)}
                                    disabled={isUploading}
                                    aria-label="Remove photo"
                                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/80 border border-white/20 text-white text-xs leading-none flex items-center justify-center hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {imagePreviews.length > 0 && !allPreviewsLoaded && (
                            <p className="text-[10px] text-stone-500 mt-1.5">
                              Loading your photos...
                            </p>
                          )}
                        </div>
                      </div>
                    </Step>

                    <Step>
                      <div className="space-y-5">
                        <p className="text-xs font-bold text-[var(--t-300)] uppercase tracking-widest mb-1">
                          Step 4 — Review & Publish
                        </p>

                        <div className="bg-black border border-[var(--t-900-40)] rounded-xl p-4 space-y-3">
                          <div className="flex justify-between gap-3">
                            <span className="text-[11px] text-stone-500 uppercase tracking-wider">
                              {sellerListingType === "service"
                                ? "Service"
                                : "Product"}
                            </span>
                            <span className="text-sm text-white font-semibold text-right">
                              {productName || "—"}
                            </span>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-[11px] text-stone-500 uppercase tracking-wider">
                              Subcategory
                            </span>
                            <span className="text-sm text-white text-right">
                              {(sellerParentCategory?.subcategories || []).find(
                                (sub: any) => sub.id === selectedSubcategory
                              )?.name || "—"}
                            </span>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-[11px] text-stone-500 uppercase tracking-wider">
                              Price
                            </span>
                            <span className="text-sm text-white text-right">
                              {productPrice
                                ? `KES ${Number(productPrice).toLocaleString()}`
                                : "—"}
                            </span>
                          </div>
                          {sellerListingType === "service" ? (
                            <div className="flex justify-between gap-3">
                              <span className="text-[11px] text-stone-500 uppercase tracking-wider">
                                Availability
                              </span>
                              <span className="text-sm text-white text-right">
                                {availDays.length > 0
                                  ? `${availDays.join(
                                      ", "
                                    )} · ${availStart}–${availEnd}`
                                  : "—"}
                              </span>
                            </div>
                          ) : (
                            <div className="flex justify-between gap-3">
                              <span className="text-[11px] text-stone-500 uppercase tracking-wider">
                                {listingType === "PRE_ORDER"
                                  ? "Pre-Order Capacity"
                                  : "Units in Stock"}
                              </span>
                              <span className="text-sm text-white text-right">
                                {listingType === "PRE_ORDER"
                                  ? preorderUnits || "—"
                                  : unitsToSell || "—"}
                              </span>
                            </div>
                          )}
                          <div>
                            <span className="text-[11px] text-stone-500 uppercase tracking-wider block mb-1">
                              Description
                            </span>
                            <p className="text-sm text-stone-300 leading-relaxed">
                              {productDescription || "—"}
                            </p>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-[11px] text-stone-500 uppercase tracking-wider">
                              Photos
                            </span>
                            <span className="text-sm text-white text-right">
                              {selectedImages.length} of 3–6 added
                            </span>
                          </div>
                        </div>

                        <p className="text-[11px] text-stone-500">
                          Double-check everything above — once you publish,
                          buyers can see this listing right away. Use "Previous"
                          to go back and fix anything.
                        </p>

                      </div>
                    </Step>
                  </Stepper>
                )}
              </div>

              {/* Upload spinner — lives in the modal itself (outside the
                  Stepper) so it always renders while publishing. */}
              {isUploading && !isSuccessScreen && (
                <div className="flex items-center justify-center gap-2 text-xs text-stone-300 mt-4">
                  <svg
                    className="animate-spin h-4 w-4 text-[var(--t-400)]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Uploading your files...</span>
                </div>
              )}

              {!isSuccessScreen && (
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(false)}
                    disabled={isUploading}
                    className="text-xs text-stone-500 hover:text-stone-300 font-medium disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* BOOKINGS MANAGEMENT MODAL OVERLAY (service listings) */}
        {isBookingsModalOpen && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-gradient-to-b from-black to-[var(--t-950)] border border-[var(--t-900-30)] rounded-2xl w-full max-w-lg p-6 my-8 shadow-2xl relative animate-fadeIn">
              <div className="flex items-start justify-between mb-1">
                <h3 className="text-xl font-bold text-white uppercase tracking-wider text-[var(--t-50)] bg-gradient-to-r from-[var(--t-400)] to-[var(--t-200)] bg-clip-text text-transparent">
                  Bookings
                </h3>
                <button
                  type="button"
                  onClick={() => setIsBookingsModalOpen(false)}
                  className="text-stone-400 hover:text-white text-xl leading-none"
                >
                  ×
                </button>
              </div>
              <p className="text-stone-400 text-xs mb-6">
                Requests for{" "}
                <span className="text-[var(--t-300)] font-medium">
                  {activeBookingsListing?.title}
                </span>
              </p>

              {isLoadingBookings ? (
                <div className="text-center py-8">
                  <Loader message="Loading bookings" scale={0.4} />
                </div>
              ) : bookings.length === 0 ? (
                <p className="text-center text-stone-400 text-sm py-8">
                  No booking requests yet for this listing.
                </p>
              ) : (
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                  {bookings.map((booking: any) => (
                    <div
                      key={booking.id}
                      className="bg-[var(--t-950-20)] border border-[var(--t-900-30)] rounded-xl p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-white font-semibold text-sm">
                            {booking.buyer_name || "Buyer"}
                          </p>
                          <p className="text-[var(--t-200-60)] text-xs mt-0.5">
                            {booking.booking_date} · {booking.booking_time}
                          </p>
                          {booking.buyer_phone && (
                            <p className="text-[var(--t-200-60)] text-xs mt-0.5">
                              {booking.buyer_phone}
                            </p>
                          )}
                          {booking.buyer_location && (
                            <p className="text-[var(--t-200-60)] text-xs mt-0.5">
                              📍 {booking.buyer_location}
                            </p>
                          )}
                          {booking.notes && (
                            <p className="text-stone-400 text-xs mt-1.5 italic">
                              "{booking.notes}"
                            </p>
                          )}
                        </div>
                        <span
                          className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                            booking.status === "pending"
                              ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                              : booking.status === "confirmed"
                              ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                              : booking.status === "completed"
                              ? "bg-green-500/20 text-green-400 border border-green-500/30"
                              : "bg-stone-700/40 text-stone-400 border border-stone-600/30"
                          }`}
                        >
                          {booking.status}
                        </span>
                      </div>

                      {booking.status === "pending" && (
                        <div className="flex gap-2 mt-3">
                          <button
                            type="button"
                            onClick={() => requestConfirmBooking(booking)}
                            className="flex-1 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-[var(--t-600)] hover:bg-[var(--t-500)] text-white transition-all"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleBookingStatusChange(booking, "declined")
                            }
                            className="flex-1 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-stone-800 hover:bg-stone-700 text-stone-300 transition-all"
                          >
                            Decline
                          </button>
                        </div>
                      )}

                      {booking.status === "confirmed" && (
                        <div className="flex gap-2 mt-3">
                          <button
                            type="button"
                            onClick={() =>
                              handleBookingStatusChange(booking, "completed")
                            }
                            className="flex-1 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider bg-green-600 hover:bg-green-500 text-white transition-all"
                          >
                            Mark Completed
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {confirmDialog && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          isDanger={confirmDialog.isDanger}
          onConfirm={confirmDialog.onConfirm}
          onCancel={closeConfirmDialog}
        />
      )}

      {/* BOTTOM TAB BAR — Home / Orders / Account, docked to the bottom
          of the viewport on every screen size (matching buyer-home),
          rather than switching to a top row at md+. Keeps the display
          name from having to repeat in a header on every tab. Tabs are
          flex-1 at every width, spreading them evenly across the full
          bar rather than shrinking into a small centered cluster once
          the dashboard itself now fills the screen. Background uses
          the same seller-category `--t-*` palette (via `themeVars` on
          the root wrapper above, which this bar inherits since it's
          still a descendant of that div) as every other themed surface
          in this file, rather than a fixed color — so it reads as
          teal for a Furniture & Home Goods seller, indigo for Tech &
          Electronics, etc., instead of always looking the same. */}
      <div className="fixed bottom-0 inset-x-0 z-40 rounded-t-3xl bg-gradient-to-t from-black via-[var(--t-950)] to-[var(--t-800)]/95 backdrop-blur-xl border-t border-[var(--t-900-40)] shadow-[0_-8px_30px_var(--t-900-40)] pb-[env(safe-area-inset-bottom)]">
        <div className="w-full flex items-stretch">
          {DASHBOARD_TABS.map((tab: any) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 text-[11px] font-bold transition-colors ${
                  isActive ? "text-white" : "text-white/60 hover:text-white/80"
                }`}
                style={
                  isActive
                    ? {
                        filter:
                          "drop-shadow(0 0 6px var(--t-400)) drop-shadow(0 0 16px var(--t-400)) drop-shadow(0 0 32px var(--t-300))",
                      }
                    : undefined
                }
              >
                <tab.icon size={22} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {sellerHomeTour.activeStep && (
        <Coachmark
          step={sellerHomeTour.activeStep}
          stepIndex={sellerHomeTour.stepIndex}
          totalSteps={sellerHomeTour.totalSteps}
          onNext={sellerHomeTour.next}
          onDismiss={sellerHomeTour.dismiss}
        />
      )}
      {sellerOrdersTour.activeStep && (
        <Coachmark
          step={sellerOrdersTour.activeStep}
          stepIndex={sellerOrdersTour.stepIndex}
          totalSteps={sellerOrdersTour.totalSteps}
          onNext={sellerOrdersTour.next}
          onDismiss={sellerOrdersTour.dismiss}
        />
      )}
      {sellerAccountTour.activeStep && (
        <Coachmark
          step={sellerAccountTour.activeStep}
          stepIndex={sellerAccountTour.stepIndex}
          totalSteps={sellerAccountTour.totalSteps}
          onNext={sellerAccountTour.next}
          onDismiss={sellerAccountTour.dismiss}
        />
      )}
      {sellerStoreTour.activeStep && (
        <Coachmark
          step={sellerStoreTour.activeStep}
          stepIndex={sellerStoreTour.stepIndex}
          totalSteps={sellerStoreTour.totalSteps}
          onNext={sellerStoreTour.next}
          onDismiss={sellerStoreTour.dismiss}
        />
      )}

      {/* STORE QR SHEET — portalled to <body> so it sits above the bottom
          tab bar; style={themeVars} carries the seller's `--t-*` palette
          into the portal, which would otherwise lose it. The code itself is
          always black-on-white inside a white card: light-on-dark QR codes
          fail on many phone scanners. */}
      {isQrSheetOpen &&
        storeQrUrl &&
        createPortal(
          <div
            style={themeVars}
            className="fixed inset-0 z-[9000] flex items-end sm:items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Your store QR code"
          >
            <div
              className="absolute inset-0 bg-black/70"
              onClick={() => setIsQrSheetOpen(false)}
            />
            <div
              style={{
                backgroundColor: "#101018",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
              }}
              className="relative w-full max-w-sm rounded-3xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]"
            >
              <div className="flex items-start justify-between gap-3 mb-1">
                <h2 className="text-lg font-bold text-white">
                  Your Store QR Code
                </h2>
                <button
                  type="button"
                  onClick={() => setIsQrSheetOpen(false)}
                  aria-label="Close"
                  className="p-1.5 -mr-1.5 rounded-lg text-[#C4C4D4] hover:bg-white/10 transition-colors"
                >
                  <CloseIcon size={18} />
                </button>
              </div>
              <p className="text-xs leading-relaxed text-[#C4C4D4] mb-4">
                Buyers scan this with their phone camera to open your store —
                the same as opening your store link.
              </p>
              <div className="mx-auto w-56 rounded-2xl bg-white p-4">
                <QRCodeCanvas
                  ref={qrCanvasRef}
                  value={storeQrUrl}
                  size={1024}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#000000"
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              </div>
              <p className="mt-3 text-center text-[11px] font-mono text-[#8888A8] break-all">
                {storeQrUrl}
              </p>
              <button
                type="button"
                onClick={handleDownloadQr}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[var(--t-600)] hover:bg-[var(--t-500)] text-white text-sm font-bold active:scale-95 transition-all"
              >
                <Download size={16} />
                Download to print or post
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
